import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useOutletContext, Link, useLocation, useNavigate } from 'react-router-dom';
import api, { getCall, getCallAudioUrl, createQANote, getQANotesForCall, reprocessCall, getReprocessScoringGuides, getUser, overrideFatalErrors, submitCollaboration, getCollaborationHistory, createReviewRequest, getCallAuditTrail, logCallAudit, syncSingleCall, assignCallToUser, updateCallFormat, getUsers, getCalls, overrideCallCompliance, overrideAlertCompliance, getRelatedCallsByPhone, regenerateTranscript, downloadCallAudio, getAvatarDisplayUrl, regenerateExpertReport } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CallTypeSelector } from '@/components/CallTypeSelector';
import ExpertLearningReport from '@/components/ExpertLearningReport';
import { 
  ArrowLeft,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneForwarded,
  Clock,
  Calendar,
  Star,
  Trophy,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  BarChart3,
  FileText,
  User,
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Search,
  X,
  XCircle,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Send,
  Shield,
  ThumbsUp,
  TrendingUp,
  RefreshCw,
  Loader2,
  UserCheck,
  Users,
  FileAudio,
  Mail,
  Upload,
  Handshake,
  Trash2,
  Link2,
  Edit3,
  UserPlus,
  Settings2,
  UserSearch,
  FilePlus,
  PhoneCall,
  Repeat,
  BookOpen,
  Voicemail,
  History,
  ArrowRight,
  Download,
  Sparkles
} from 'lucide-react';
import { format } from 'date-fns';

// Utility function to parse plain text transcript into conversation format
function parseTranscriptToConversation(text) {
  if (!text || typeof text !== 'string') return [];
  
  // Split by sentences (periods, question marks, exclamation marks)
  const sentences = text
    .replace(/([.!?])\s+/g, '$1|||')
    .split('|||')
    .map(s => s.trim())
    .filter(s => s.length > 0);
  
  // Group sentences into turns (2-3 sentences per turn for more natural flow)
  const turns = [];
  const speakers = ['Speaker 1 (Recruiter)', 'Speaker 2 (Candidate)'];
  let currentTurn = [];
  let sentencesPerTurn = 2;
  
  sentences.forEach((sentence, idx) => {
    currentTurn.push(sentence);
    
    // Create a new turn every 2-3 sentences, or at natural break points
    const isNaturalBreak = sentence.endsWith('?') || 
                          sentence.toLowerCase().includes('hello') ||
                          sentence.toLowerCase().includes('hi ') ||
                          sentence.toLowerCase().includes('thank you') ||
                          sentence.toLowerCase().includes('okay') ||
                          sentence.toLowerCase().includes('yes') ||
                          sentence.toLowerCase().includes('no,');
    
    if (currentTurn.length >= sentencesPerTurn || isNaturalBreak || idx === sentences.length - 1) {
      if (currentTurn.length > 0) {
        turns.push({
          speaker: speakers[turns.length % 2],
          text: currentTurn.join(' '),
          start_time: turns.length * 10,
          end_time: (turns.length + 1) * 10
        });
        currentTurn = [];
      }
    }
  });
  
  return turns;
}

// Transcript with Search Component
function TranscriptWithSearch({ transcript }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [matchedIndices, setMatchedIndices] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const segmentRefs = useRef([]);
  
  // Parse transcript into segments
  const segments = Array.isArray(transcript) 
    ? transcript 
    : parseTranscriptToConversation(transcript);
  
  // Find all matches when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchedIndices([]);
      setCurrentMatchIndex(0);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const matches = [];
    
    segments.forEach((segment, idx) => {
      if (segment.text?.toLowerCase().includes(query)) {
        matches.push(idx);
      }
    });
    
    setMatchedIndices(matches);
    setCurrentMatchIndex(0);
    
    // Scroll to first match
    if (matches.length > 0 && segmentRefs.current[matches[0]]) {
      segmentRefs.current[matches[0]].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchQuery, segments]);
  
  // Navigate to next/previous match
  const goToMatch = (direction) => {
    if (matchedIndices.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentMatchIndex + 1) % matchedIndices.length;
    } else {
      newIndex = (currentMatchIndex - 1 + matchedIndices.length) % matchedIndices.length;
    }
    
    setCurrentMatchIndex(newIndex);
    const segmentIdx = matchedIndices[newIndex];
    if (segmentRefs.current[segmentIdx]) {
      segmentRefs.current[segmentIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  // Highlight search term in text
  const highlightText = (text, isCurrentMatch) => {
    if (!searchQuery.trim() || !text) return text;
    
    const query = searchQuery.toLowerCase();
    const parts = text.split(new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    
    return parts.map((part, i) => 
      part.toLowerCase() === query ? (
        <mark 
          key={i} 
          className={`px-0.5 rounded ${isCurrentMatch ? 'bg-yellow-400' : 'bg-yellow-200'}`}
        >
          {part}
        </mark>
      ) : part
    );
  };
  
  if (segments.length === 0 && typeof transcript === 'string') {
    return (
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="sticky top-0 z-10 bg-white pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search transcript..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        
        <div className="bg-muted/50 rounded-lg p-4">
          <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
            {highlightText(transcript, true)}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="sticky top-0 z-10 bg-white pb-4 border-b">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search transcript... (e.g., 'TekWissen', 'salary', 'experience')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {matchedIndices.length > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {currentMatchIndex + 1} of {matchedIndices.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToMatch('prev')}
                className="h-8 w-8 p-0"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToMatch('next')}
                className="h-8 w-8 p-0"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {searchQuery && matchedIndices.length === 0 && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">No matches</span>
          )}
        </div>
      </div>
      
      {/* Transcript Segments */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {segments.map((segment, idx) => {
          const isRecruiter = segment.speaker?.toLowerCase()?.includes('recruiter') || 
                             segment.speaker?.includes('1') ||
                             idx % 2 === 0;
          const isMatch = matchedIndices.includes(idx);
          const isCurrentMatch = matchedIndices[currentMatchIndex] === idx;
          
          return (
            <div 
              key={idx}
              ref={el => segmentRefs.current[idx] = el}
              className={`p-4 rounded-lg transition-all ${isRecruiter 
                ? 'bg-blue-50 border-l-4 border-blue-400 ml-0 mr-8' 
                : 'bg-green-50 border-l-4 border-green-400 ml-8 mr-0'}
                ${isCurrentMatch ? 'ring-2 ring-yellow-400 ring-offset-2' : ''}
                ${isMatch && !isCurrentMatch ? 'ring-1 ring-yellow-300' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-semibold ${isRecruiter ? 'text-blue-700' : 'text-green-700'}`}>
                  {segment.speaker || (isRecruiter ? 'Recruiter' : 'Candidate')}
                </span>
                {(segment.start_time !== undefined && segment.end_time !== undefined) && (
                  <span className="text-xs text-muted-foreground font-mono">
                    {Math.floor(segment.start_time)}s - {Math.floor(segment.end_time)}s
                  </span>
                )}
              </div>
              <p className="text-slate-700 leading-relaxed">
                {highlightText(segment.text, isCurrentMatch)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Audio Player Component
function AudioPlayer({ callId, audioUrl: directAudioUrl, onAudioPlay, user, callFilename }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [hasLoggedPlay, setHasLoggedPlay] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 3];
  
  const hasAudio = !!directAudioUrl;
  
  // Check if user is admin
  const userRole = user?.role_id || user?.role;
  const isAdmin = userRole === 'admin' || userRole === 'owner' || user?.is_admin;

  // Fetch audio with authentication when component mounts
  useEffect(() => {
    const fetchAudio = async () => {
      setIsLoading(true);
      setLoadError(null);
      setErrorType(null);
      try {
        const result = await getCallAudioUrl(callId);
        if (result.error) {
          setLoadError(result.error);
          setErrorType(result.errorType);
        } else {
          setAudioUrl(result.url);
        }
      } catch (error) {
        console.error('Failed to load audio:', error);
        setLoadError('Unable to load audio file');
        setErrorType('unknown');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (callId) {
      fetchAudio();
    }
    
    // Cleanup blob URL on unmount
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [callId]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => {
          console.error('Audio play error:', e);
          setLoadError('Failed to play audio');
          setErrorType('playback_error');
        });
        
        // Log audio play event (only once per session)
        if (!hasLoggedPlay && onAudioPlay) {
          onAudioPlay();
          setHasLoggedPlay(true);
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoaded(true);
      setLoadError(false);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownload = async () => {
    if (!isAdmin) {
      toast.error('Only administrators can download audio recordings');
      return;
    }
    
    setIsDownloading(true);
    try {
      const response = await downloadCallAudio(callId);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = callFilename || `call-${callId}.mp3`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Audio downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      if (error.response?.status === 403) {
        toast.error('Only administrators can download audio recordings');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to download audio');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Enhanced error display with specific messages based on error type
  if (loadError) {
    const isOneDriveError = errorType === 'onedrive_disconnected' || errorType === 'onedrive_expired';
    const isNoFile = errorType === 'no_file';
    
    return (
      <div className={`p-4 rounded-lg text-center ${isOneDriveError ? 'bg-amber-50 border border-amber-200' : 'bg-muted'}`}>
        {isOneDriveError ? (
          <>
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-amber-800">OneDrive Connection Required</p>
            <p className="text-xs text-amber-600 mt-1">{loadError}</p>
            <a 
              href="/dashboard/onedrive" 
              className="inline-block mt-3 px-4 py-2 bg-amber-500 text-white text-xs font-medium rounded-md hover:bg-amber-600 transition-colors"
            >
              Reconnect OneDrive
            </a>
          </>
        ) : isNoFile ? (
          <>
            <Phone className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground font-medium">Audio Not Available</p>
            <p className="text-xs text-muted-foreground mt-1">No audio file has been uploaded for this call</p>
          </>
        ) : (
          <>
            <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground font-medium">Unable to Load Audio</p>
            <p className="text-xs text-muted-foreground mt-1">{loadError}</p>
          </>
        )}
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="p-4 bg-slate-900 rounded-sm text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">Loading audio from storage...</p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-slate-900 rounded-sm">
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
          onError={() => setLoadError(true)}
        />
      )}
      
      <div className="flex items-center gap-4">
        {/* Play/Pause Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={togglePlay}
          className="h-12 w-12 rounded-full bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="audio-play-btn"
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </Button>
        
        {/* Stop Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleStop}
          className="h-10 w-10 rounded-full bg-slate-700 hover:bg-slate-600 text-white"
          data-testid="audio-stop-btn"
        >
          <Square className="h-4 w-4" />
        </Button>
        
        {/* Progress Bar */}
        <div className="flex-1">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={0.1}
            onValueChange={handleSeek}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        {/* Mute Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMute}
          className="text-muted-foreground hover:text-white"
        >
          {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </Button>
        
        {/* Playback Speed Control */}
        <div className="flex items-center gap-1 bg-slate-800 rounded-full px-2 py-1">
          {PLAYBACK_SPEEDS.map((speed) => (
            <button
              key={speed}
              onClick={() => handleSpeedChange(speed)}
              className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                playbackSpeed === speed
                  ? 'bg-blue-500 text-white'
                  : 'text-muted-foreground hover:text-white hover:bg-slate-700'
              }`}
              data-testid={`speed-${speed}x-btn`}
            >
              {speed}x
            </button>
          ))}
        </div>
        
        {/* Download Button - Admin Only */}
        {isAdmin && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDownload}
            disabled={isDownloading}
            className="text-muted-foreground hover:text-white hover:bg-slate-700"
            title="Download Audio (Admin Only)"
            data-testid="audio-download-btn"
          >
            {isDownloading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Download className="h-5 w-5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CallDetail({ isAdmin = false }) {
  const { callId } = useParams();
  const context = useOutletContext() || {};
  const { user: authUser } = useAuth();
  
  // Get user from context (for non-authenticated users via unique_url) or from auth
  const user = context.user || authUser;
  
  const [call, setCall] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [assignedUser, setAssignedUser] = useState(null);
  
  // Reprocess state
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [reprocessStatus, setReprocessStatus] = useState(null);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [scoringGuides, setScoringGuides] = useState([]);
  const [selectedScoringGuide, setSelectedScoringGuide] = useState(null);
  const [reprocessTranscript, setReprocessTranscript] = useState(true);
  
  // Regenerate Transcript state
  const [isRegeneratingTranscript, setIsRegeneratingTranscript] = useState(false);

  // Regenerate Expert Learning Report state
  const [isRegeneratingReport, setIsRegeneratingReport] = useState(false);
  
  // QA Notes state
  const [qaNotes, setQaNotes] = useState([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  
  // Collaboration state
  const [showCollaborateDialog, setShowCollaborateDialog] = useState(false);
  const [collaborationScores, setCollaborationScores] = useState({});
  const [collaborationNotes, setCollaborationNotes] = useState('');
  const [collaborationFeedback, setCollaborationFeedback] = useState('');
  const [isSubmittingCollaboration, setIsSubmittingCollaboration] = useState(false);
  
  // Override Fatal state
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [selectedAlertsToRemove, setSelectedAlertsToRemove] = useState([]);
  const [overrideReason, setOverrideReason] = useState('');
  const [isOverriding, setIsOverriding] = useState(false);
  
  // Request Review state (for users)
  const [showReviewRequestDialog, setShowReviewRequestDialog] = useState(false);
  const [reviewRequestReason, setReviewRequestReason] = useState('');
  const [reviewRequestExpected, setReviewRequestExpected] = useState('');
  const [isSubmittingReviewRequest, setIsSubmittingReviewRequest] = useState(false);
  
  // Audit Trail state
  const [auditTrail, setAuditTrail] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [auditFilter, setAuditFilter] = useState('all');
  
  // Sync Call state
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Assign User state
  const [showAssignUserDialog, setShowAssignUserDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  // Edit Format state
  const [showEditFormatDialog, setShowEditFormatDialog] = useState(false);
  const [editPhoneNumber, setEditPhoneNumber] = useState('');
  const [editExtension, setEditExtension] = useState('');
  const [editCallType, setEditCallType] = useState('');
  const [isUpdatingFormat, setIsUpdatingFormat] = useState(false);
  
  // Call Navigation state (for auditors to navigate between calls)
  const [siblingCalls, setSiblingCalls] = useState([]);
  const [currentCallIndex, setCurrentCallIndex] = useState(-1);
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(false);
  
  // Individual Alert Override state
  const [overridingAlertIndex, setOverridingAlertIndex] = useState(null);
  const [individualOverrideReason, setIndividualOverrideReason] = useState('');
  const [showIndividualOverrideDialog, setShowIndividualOverrideDialog] = useState(false);
  const [selectedAlertForOverride, setSelectedAlertForOverride] = useState(null);
  
  // Related Calls by Candidate Number state
  const [relatedCalls, setRelatedCalls] = useState([]);
  const [isLoadingRelatedCalls, setIsLoadingRelatedCalls] = useState(false);
  const [showRelatedCallsDialog, setShowRelatedCallsDialog] = useState(false);
  const [candidatePhoneNumber, setCandidatePhoneNumber] = useState(null);
  
  // Assigned Auditor state
  const [assignedAuditor, setAssignedAuditor] = useState(null);
  const [auditorStats, setAuditorStats] = useState(null);
  const [showAuditorPopup, setShowAuditorPopup] = useState(false);
  const [auditorPopupPosition, setAuditorPopupPosition] = useState({ x: 0, y: 0 });
  
  // Assigned User popup state
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [userPopupPosition, setUserPopupPosition] = useState({ x: 0, y: 0 });
  const [userStats, setUserStats] = useState(null);
  const [isLoadingUserStats, setIsLoadingUserStats] = useState(false);
  
  // Get location and navigate for smart back button
  const location = useLocation();
  const navigate = useNavigate();

  // Build base URL based on context - support legacy admin, SaaS tenant, and user flows
  const baseUrl = context.adminUrl 
    ? `/admin/${context.adminUrl}` 
    : context.userUrl 
      ? `/user/${context.userUrl}` 
      : '/dashboard';  // SaaS tenant flow
  
  // Check URL query params for source tracking
  const searchParams = new URLSearchParams(location.search);
  const fromPage = searchParams.get('from');
  
  // Determine back URL - check if we came from a specific page like alerts or unknown calls
  const getBackUrl = () => {
    // Check URL param first (for new tab opens)
    if (fromPage === 'alerts') {
      return `${baseUrl}/alerts`;
    }
    // Check location state for referrer page
    if (location.state?.from) {
      return location.state.from;
    }
    // Check referrer in the URL or default to calls page
    const referrerPath = location.state?.referrerPath;
    if (referrerPath) {
      return referrerPath;
    }
    // Default to calls page
    return `${baseUrl}/calls`;
  };
  
  const backUrl = getBackUrl();
  
  // Determine back button label based on source
  const getBackLabel = () => {
    if (fromPage === 'alerts') {
      return 'Back to Alerts';
    }
    return location.state?.fromLabel || 'Back to Calls';
  };
  
  const backLabel = getBackLabel();

  // Check if user can create QA notes (admin or quality_auditor)
  const canCreateNotes = user?.is_admin || 
    user?.role === 'admin' || 
    user?.role === 'quality_auditor' || 
    user?.role_id === 'quality_auditor' ||
    user?.role === 'owner';
    
  // Check if user can reprocess calls
  const canReprocess = isAdmin || user?.is_admin || user?.role === 'admin' || user?.role === 'owner';
  
  // Check if this is the user viewing their own call (can request review)
  const isUserCall = !isAdmin && context.userUrl && call?.user_id === user?.id;

  const fetchCall = async () => {
    try {
      const { data } = await getCall(callId);
      setCall(data);
      
      // Fetch assigned user details if call has user_id
      if (data.user_id) {
        try {
          const userRes = await getUser(data.user_id);
          setAssignedUser(userRes.data);
        } catch (e) {
          console.log('Could not fetch assigned user:', e);
        }
      }
      
      // Log view event to audit trail
      if (user?.id && user?.name) {
        try {
          await logCallAudit(
            { call_id: callId, action: 'view', details: { page: 'call_detail' } },
            user.id,
            user.name,
            user.role || 'user',
            isAdmin ? 'admin' : 'user'
          );
        } catch (e) {
          console.log('Could not log view event:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch call:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch audit trail for this call
  const fetchAuditTrail = async () => {
    setIsLoadingAudit(true);
    try {
      const params = { limit: 100 };
      if (auditFilter !== 'all') {
        params.action_filter = auditFilter;
      }
      const { data } = await getCallAuditTrail(callId, params);
      setAuditTrail(data.entries || []);
      setAuditSummary({
        total: data.total,
        unique_actors: data.unique_actors,
        action_counts: data.action_counts
      });
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  useEffect(() => {
    fetchCall();
  }, [callId]);
  
  // Fetch audit trail when tab is viewed or filter changes
  useEffect(() => {
    if (call) {
      fetchAuditTrail();
    }
  }, [callId, auditFilter, call]);
  
  // Fetch sibling calls for navigation (calls from same context - e.g., same user, same date range)
  const fetchSiblingCalls = async () => {
    if (!call) return;
    
    setIsLoadingSiblings(true);
    try {
      // Build params to get calls in same context
      const params = {
        page_size: 100,
        sort_by: 'call_date',
        sort_order: 'desc'
      };
      
      // If call has tenant_id, filter by tenant
      if (call.tenant_id) {
        params.tenant_id = call.tenant_id;
      }
      
      // If call has user_id, filter by user for more relevant navigation
      if (call.user_id) {
        params.user_id = call.user_id;
      }
      
      const { data } = await getCalls(params);
      const callsList = data.calls || data || [];
      
      setSiblingCalls(callsList);
      
      // Find current call's index
      const idx = callsList.findIndex(c => (c.id || c._id) === callId);
      setCurrentCallIndex(idx);
    } catch (error) {
      console.error('Failed to fetch sibling calls:', error);
      setSiblingCalls([]);
    } finally {
      setIsLoadingSiblings(false);
    }
  };
  
  // Fetch sibling calls when call loads
  useEffect(() => {
    if (call) {
      fetchSiblingCalls();
    }
  }, [call?.id, call?.tenant_id]);
  
  // Fetch related calls by candidate phone number
  const fetchRelatedCalls = async (phoneNumber) => {
    if (!phoneNumber) return;
    
    setIsLoadingRelatedCalls(true);
    try {
      const currentCallId = call?.id || call?._id;
      const params = {
        exclude_call_id: currentCallId,
        limit: 50
      };
      // Only add tenant_id if it exists
      if (call?.tenant_id) {
        params.tenant_id = call.tenant_id;
      }
      
      // Use the new dedicated endpoint for related calls by phone
      const { data } = await getRelatedCallsByPhone(phoneNumber, params);
      setRelatedCalls(data.calls || []);
    } catch (error) {
      console.error('Failed to fetch related calls:', error);
      // Fallback to the old method if the new endpoint fails
      try {
        const params = {
          page_size: 50,
          sort_by: 'call_date',
          sort_order: 'desc',
          search: phoneNumber
        };
        if (call?.tenant_id) {
          params.tenant_id = call.tenant_id;
        }
        
        const { data } = await getCalls(params);
        const callsList = data.calls || data || [];
        
        // Filter to get calls with matching phone number (excluding current call)
        const currentCallId = call?.id || call?._id;
        const matchingCalls = callsList.filter(c => {
          const cPhone = c.candidate_phone || c.phone_number || c.participant_number || '';
          const thisCallId = c.id || c._id;
          return cPhone.includes(phoneNumber.replace(/\D/g, '').slice(-10)) && thisCallId !== currentCallId;
        });
        
        setRelatedCalls(matchingCalls);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
        setRelatedCalls([]);
      }
    } finally {
      setIsLoadingRelatedCalls(false);
    }
  };
  
  // Extract candidate phone number from call data
  useEffect(() => {
    if (call) {
      // Try multiple sources for candidate phone number
      const phone = call.candidate_phone || 
                    call.phone_number || 
                    call.participant_number ||
                    call.to_number ||
                    call.caller_id;
      
      if (phone) {
        setCandidatePhoneNumber(phone);
        fetchRelatedCalls(phone);
      }
    }
  }, [call?.id, call?.candidate_phone, call?.phone_number]);
  
  // Fetch assigned auditor for the user
  useEffect(() => {
    const fetchAssignedAuditor = async () => {
      const userId = assignedUser?.id || call?.user?.id || call?.user_id;
      if (!userId) {
        setAssignedAuditor(null);
        return;
      }
      
      try {
        // Get all assignments (tenant_id is now optional)
        const { data } = await api.get('/qa-tasks/assignments');
        const assignments = data.assignments || [];
        
        console.log('Looking for auditor for user:', userId, 'in', assignments.length, 'assignments');
        
        // Find the auditor assigned to this user
        let foundAuditorId = null;
        let foundAuditorName = null;
        let foundAuditorAvatar = null;
        let earliestAssignment = null;
        
        for (const assignment of assignments) {
          const assignedUserIds = assignment.assigned_user_ids || [];
          
          if (assignedUserIds.includes(userId)) {
            foundAuditorId = assignment.auditor_id;
            foundAuditorName = assignment.auditor_name || 'Unknown Auditor';
            foundAuditorAvatar = assignment.auditor_avatar_url;
            // Track earliest assignment date
            if (!earliestAssignment || new Date(assignment.created_at) < new Date(earliestAssignment)) {
              earliestAssignment = assignment.created_at;
            }
            break;
          }
        }
        
        if (!foundAuditorId) {
          console.log('No auditor found for user:', userId);
          setAssignedAuditor(null);
          setAuditorStats(null);
          return;
        }
        
        console.log('Found auditor:', foundAuditorName, 'for user:', userId);
        
        // Set auditor info
        setAssignedAuditor({
          id: foundAuditorId,
          name: foundAuditorName,
          avatar_url: foundAuditorAvatar
        });
        
        // Count total UNIQUE users assigned to THIS auditor across ALL their assignments
        const allAuditorAssignments = assignments.filter(a => 
          a.auditor_id === foundAuditorId && a.status === 'active'
        );
        const uniqueUserIds = new Set();
        for (const assignment of allAuditorAssignments) {
          const userIds = assignment.assigned_user_ids || [];
          userIds.forEach(id => uniqueUserIds.add(id));
        }
        
        setAuditorStats({
          totalAssignedUsers: uniqueUserIds.size,
          avgScore: null,
          assignedSince: earliestAssignment
        });
      } catch (error) {
        console.error('Failed to fetch auditor:', error);
        setAssignedAuditor(null);
      }
    };
    
    fetchAssignedAuditor();
  }, [assignedUser?.id, call?.user?.id, call?.user_id, call?.id]);

  // Handle auditor name click
  const handleAuditorClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setAuditorPopupPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8
    });
    setShowAuditorPopup(true);
  };

  // Handle assigned user click - fetch stats and show popup
  const handleUserClick = async (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setUserPopupPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 8
    });
    setShowUserPopup(true);
    
    // Fetch user stats if not already loaded
    const currentUser = assignedUser || call?.user;
    if (currentUser && !userStats) {
      setIsLoadingUserStats(true);
      try {
        const userId = currentUser.id || currentUser.user_id;
        
        // Fetch user's calls for stats
        const callsRes = await api.get('/calls', {
          params: { user_id: userId, limit: 200 }
        });
        const userCalls = callsRes.data.calls || callsRes.data || [];
        
        // Calculate stats
        const totalCalls = userCalls.length;
        const scores = userCalls.filter(c => c.overall_score != null).map(c => c.overall_score);
        const avgScore = scores.length > 0 
          ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
          : null;
        
        // Try to get leaderboard position
        let leaderboardRank = null;
        try {
          const leaderboardRes = await api.get('/analytics/leaderboard');
          const leaderboard = leaderboardRes.data?.overall || leaderboardRes.data || [];
          const userRank = leaderboard.findIndex(l => l.user_id === userId || l.id === userId);
          if (userRank >= 0) {
            leaderboardRank = userRank + 1;
          }
        } catch (e) {
          console.log('Could not fetch leaderboard:', e);
        }
        
        // Get reporting manager info
        let reportsTo = null;
        if (currentUser.reports_to || currentUser.reporting_to) {
          try {
            const managerId = currentUser.reports_to || currentUser.reporting_to;
            const managerRes = await api.get(`/users/${managerId}`);
            reportsTo = managerRes.data?.name || managerRes.data?.full_name;
          } catch (e) {
            console.log('Could not fetch manager:', e);
          }
        }
        
        setUserStats({
          totalCalls,
          avgScore,
          leaderboardRank,
          reportsTo: reportsTo || currentUser.reports_to_name || currentUser.manager_name || null
        });
      } catch (error) {
        console.error('Failed to fetch user stats:', error);
        setUserStats({ totalCalls: 0, avgScore: null, leaderboardRank: null, reportsTo: null });
      } finally {
        setIsLoadingUserStats(false);
      }
    }
  };
  
  // Navigate to previous call
  const goToPreviousCall = () => {
    if (currentCallIndex > 0 && siblingCalls.length > 0) {
      const prevCall = siblingCalls[currentCallIndex - 1];
      const prevCallId = prevCall.id || prevCall._id;
      if (prevCallId) {
        navigate(`${baseUrl}/call/${prevCallId}`, { 
          state: { from: location.state?.from, fromLabel: location.state?.fromLabel } 
        });
      }
    }
  };
  
  // Navigate to next call
  const goToNextCall = () => {
    if (currentCallIndex >= 0 && currentCallIndex < siblingCalls.length - 1) {
      const nextCall = siblingCalls[currentCallIndex + 1];
      const nextCallId = nextCall.id || nextCall._id;
      if (nextCallId) {
        navigate(`${baseUrl}/call/${nextCallId}`, { 
          state: { from: location.state?.from, fromLabel: location.state?.fromLabel } 
        });
      }
    }
  };
  
  // Handle individual alert override
  const handleIndividualAlertOverride = async () => {
    if (!selectedAlertForOverride || !individualOverrideReason.trim()) {
      toast.error('Please provide a reason for the override');
      return;
    }
    
    setOverridingAlertIndex(selectedAlertForOverride.index);
    try {
      // Use the overrideCallCompliance API to override specific alert
      await overrideCallCompliance(callId, {
        new_compliance_status: 'compliant',
        override_reason: individualOverrideReason,
        alert_type: selectedAlertForOverride.type,
        alert_index: selectedAlertForOverride.index,
        qa_notes: `Alert override: ${selectedAlertForOverride.type} - ${individualOverrideReason}`
      });
      
      toast.success('Alert marked as compliant');
      setShowIndividualOverrideDialog(false);
      setSelectedAlertForOverride(null);
      setIndividualOverrideReason('');
      
      // Refresh call data to show updated compliance status
      await fetchCall();
    } catch (error) {
      console.error('Failed to override alert:', error);
      toast.error(error.response?.data?.detail || 'Failed to override alert');
    } finally {
      setOverridingAlertIndex(null);
    }
  };
  
  // Get call type icon and color
  const getCallTypeInfo = (callType) => {
    const type = (callType || '').toLowerCase();
    
    // Screening calls
    if (type.includes('screening') || type.includes('pre_screening') || type === 'prescreening') {
      return { icon: UserSearch, color: 'text-cyan-500', bg: 'bg-cyan-100 dark:bg-cyan-900/30', label: 'Screening Call' };
    }
    // Submission calls
    if (type.includes('submission')) {
      return { icon: FilePlus, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/30', label: 'Submission Call' };
    }
    // Introduction calls
    if (type.includes('introduction') || type.includes('intro')) {
      return { icon: Handshake, color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/30', label: 'Introduction Call' };
    }
    // Follow-up calls
    if (type.includes('follow') || type === 'followup') {
      return { icon: Repeat, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30', label: 'Follow-up Call' };
    }
    // Interview calls
    if (type.includes('interview')) {
      return { icon: UserCheck, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30', label: 'Interview Call' };
    }
    // Voicemail
    if (type.includes('voicemail')) {
      return { icon: FileAudio, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30', label: 'Voicemail' };
    }
    // Inbound calls
    if (type.includes('inbound') || type === 'incoming') {
      return { icon: PhoneIncoming, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Inbound Call' };
    }
    // Outbound calls
    if (type.includes('outbound') || type === 'outgoing') {
      return { icon: PhoneOutgoing, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Outbound Call' };
    }
    // Internal/PBX calls
    if (type.includes('intra') || type.includes('pbx') || type.includes('internal')) {
      return { icon: PhoneForwarded, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Intra PBX Call' };
    }
    // Default - generic call
    return { icon: PhoneCall, color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800', label: callType ? callType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Call' };
  };
  
  // Handle reprocess call
  const handleReprocess = async (reprocessTranscriptFlag = true, scoringGuideId = null) => {
    if (!callId) return;
    
    setIsReprocessing(true);
    setReprocessStatus('starting');
    setShowReprocessDialog(false);
    
    try {
      const response = await reprocessCall(callId, { 
        reprocess_transcript: reprocessTranscriptFlag,
        reprocess_analysis: true,
        scoring_guide_id: scoringGuideId
      });
      
      if (response.data.success) {
        // Log audit event
        if (user?.id && user?.name) {
          await logCallAudit(
            { call_id: callId, action: 'reprocess', details: { reprocess_transcript: reprocessTranscriptFlag, scoring_guide_id: scoringGuideId }},
            user.id,
            user.name,
            user.role || 'admin',
            isAdmin ? 'admin' : 'user'
          ).catch(console.log);
        }
        
        setReprocessStatus('processing');
        toast.success('Reprocessing started. This may take a few minutes...');
        
        // Poll for completion
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max
        
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const { data: updatedCall } = await getCall(callId);
            
            if (updatedCall.processing_status === 'completed') {
              clearInterval(pollInterval);
              setCall(updatedCall);
              setReprocessStatus('completed');
              setIsReprocessing(false);
              toast.success('Call reprocessed successfully! Scores and transcript updated.');
            } else if (updatedCall.processing_status === 'failed') {
              clearInterval(pollInterval);
              setReprocessStatus('failed');
              setIsReprocessing(false);
              toast.error(`Reprocessing failed: ${updatedCall.processing_error || 'Unknown error'}`);
            } else if (attempts >= maxAttempts) {
              clearInterval(pollInterval);
              setReprocessStatus('timeout');
              setIsReprocessing(false);
              toast.warning('Reprocessing is taking longer than expected. Please refresh the page later.');
            }
          } catch (e) {
            console.error('Polling error:', e);
          }
        }, 5000); // Poll every 5 seconds
      } else {
        setReprocessStatus('failed');
        setIsReprocessing(false);
        toast.error(response.data.message || 'Failed to start reprocessing');
      }
    } catch (error) {
      console.error('Reprocess error:', error);
      setReprocessStatus('failed');
      setIsReprocessing(false);
      toast.error(error.response?.data?.detail || 'Failed to reprocess call');
    }
  };

  // Open reprocess dialog with scoring guide selection
  const openReprocessDialog = async () => {
    try {
      const { data } = await getReprocessScoringGuides();
      setScoringGuides(data.scoring_guides || []);
      setSelectedScoringGuide(data.default_guide?.id || null);
      setReprocessTranscript(true);
      setShowReprocessDialog(true);
    } catch (error) {
      console.error('Failed to load scoring guides:', error);
      toast.error('Failed to load scoring guides');
    }
  };

  // Handle Regenerate Transcript - only re-transcribes without re-analyzing
  const handleRegenerateTranscript = async () => {
    if (!callId) return;
    
    setIsRegeneratingTranscript(true);
    try {
      const response = await regenerateTranscript([callId]);
      
      if (response.data?.success) {
        toast.success('Transcript regeneration started. This may take a few minutes...');
        
        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            const { data: updatedCall } = await getCall(callId);
            if (updatedCall) {
              // Check if transcript was updated
              const wasRegenerated = updatedCall.transcript_regenerated_at && 
                new Date(updatedCall.transcript_regenerated_at) > new Date(Date.now() - 5 * 60 * 1000);
              
              if (wasRegenerated) {
                clearInterval(pollInterval);
                setIsRegeneratingTranscript(false);
                setCall(updatedCall);
                toast.success('Transcript regenerated successfully!');
              }
            }
          } catch (pollError) {
            console.error('Poll error:', pollError);
          }
        }, 5000);
        
        // Stop polling after 3 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setIsRegeneratingTranscript(false);
          toast.info('Transcript regeneration is taking longer than expected. Please refresh the page later.');
        }, 180000);
        
      } else {
        throw new Error(response.data?.message || 'Failed to start regeneration');
      }
    } catch (error) {
      console.error('Regenerate transcript error:', error);
      setIsRegeneratingTranscript(false);
      toast.error(error.response?.data?.detail || error.message || 'Failed to regenerate transcript');
    }
  };

  // Generate or regenerate the Expert Learning Report on demand
  const handleRegenerateExpertReport = async () => {
    if (!callId || isRegeneratingReport) return;
    setIsRegeneratingReport(true);
    try {
      const { data } = await regenerateExpertReport(callId);
      if (data?.expert_learning_report) {
        setCall((prev) => prev ? { ...prev, expert_learning_report: data.expert_learning_report } : prev);
        toast.success('Expert Learning Report generated successfully.');
      } else {
        toast.error('Report generated but the response was empty.');
      }
    } catch (error) {
      console.error('Regenerate expert report error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to generate Expert Learning Report');
    } finally {
      setIsRegeneratingReport(false);
    }
  };

  // Fetch QA notes for this call
  const fetchQANotes = async () => {
    setIsLoadingNotes(true);
    try {
      const { data } = await getQANotesForCall(callId);
      setQaNotes(data.notes || []);
    } catch (error) {
      console.error('Failed to fetch QA notes:', error);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (callId) {
      fetchQANotes();
    }
  }, [callId]);

  // Submit a new QA note
  const handleSubmitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    const targetUserId = call?.user_id || call?.assigned_to;
    if (!targetUserId) {
      toast.error('Cannot add QA note: This call has no assigned user');
      return;
    }

    setIsSubmittingNote(true);
    try {
      await createQANote({
        call_id: callId,
        user_id: targetUserId,
        note_text: newNote,
        note_type: noteType
      });
      
      // Log audit event
      if (user?.id && user?.name) {
        await logCallAudit(
          { call_id: callId, action: 'qa_note_added', details: { note_type: noteType } },
          user.id,
          user.name,
          user.role || 'auditor',
          isAdmin ? 'admin' : 'auditor'
        ).catch(console.log);
      }
      
      toast.success('QA note added successfully');
      setNewNote('');
      setNoteType('general');
      fetchQANotes(); // Refresh notes
    } catch (error) {
      let message = 'Failed to add note';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error
        message = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(message);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Handle collaboration submission
  const handleCollaborationSubmit = async () => {
    if (!collaborationNotes.trim()) {
      toast.error('Please add your notes');
      return;
    }
    
    setIsSubmittingCollaboration(true);
    try {
      const { data } = await submitCollaboration(callId, {
        auditor_scores: collaborationScores,
        auditor_notes: collaborationNotes,
        auditor_feedback: collaborationFeedback,
        recalculate_with_ai: true
      });
      
      // Log audit event
      if (user?.id && user?.name) {
        await logCallAudit(
          { call_id: callId, action: 'collaborate', details: { new_average: data.combined_average } },
          user.id,
          user.name,
          user.role || 'auditor',
          isAdmin ? 'admin' : 'auditor'
        ).catch(console.log);
      }
      
      toast.success(`Collaboration submitted! New average: ${data.combined_average.toFixed(1)}`);
      setShowCollaborateDialog(false);
      setCollaborationScores({});
      setCollaborationNotes('');
      setCollaborationFeedback('');
      
      // Refresh call data
      const { data: refreshedCall } = await getCall(callId);
      setCall(refreshedCall);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit collaboration');
    } finally {
      setIsSubmittingCollaboration(false);
    }
  };

  // Handle fatal override submission
  const handleOverrideSubmit = async () => {
    if (selectedAlertsToRemove.length === 0) {
      toast.error('Please select alerts to remove');
      return;
    }
    if (!overrideReason.trim()) {
      toast.error('Please provide a reason for the override');
      return;
    }
    
    setIsOverriding(true);
    try {
      const { data } = await overrideFatalErrors(callId, {
        fatal_alerts_to_remove: selectedAlertsToRemove,
        override_reason: overrideReason
      });
      
      // Log audit event
      if (user?.id && user?.name) {
        await logCallAudit(
          { call_id: callId, action: 'override', details: { 
            removed_count: data.removed_alerts?.length || 0, 
            reason: overrideReason,
            new_classification: data.new_classification 
          }},
          user.id,
          user.name,
          user.role || 'auditor',
          isAdmin ? 'admin' : 'auditor'
        ).catch(console.log);
      }
      
      toast.success(`Removed ${data.removed_alerts?.length || 0} alert(s). New classification: ${data.new_classification}`);
      setShowOverrideDialog(false);
      setSelectedAlertsToRemove([]);
      setOverrideReason('');
      
      // Refresh call data
      const { data: refreshedCall } = await getCall(callId);
      setCall(refreshedCall);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to override alerts');
    } finally {
      setIsOverriding(false);
    }
  };

  // Handle review request submission (for users)
  const handleReviewRequestSubmit = async () => {
    if (!reviewRequestReason.trim()) {
      toast.error('Please explain why you want this call reviewed');
      return;
    }
    
    setIsSubmittingReviewRequest(true);
    try {
      await createReviewRequest(
        {
          call_id: callId,
          reason: reviewRequestReason,
          expected_outcome: reviewRequestExpected
        },
        user?.id,
        user?.name
      );
      
      // Log audit event
      if (user?.id && user?.name) {
        await logCallAudit(
          { call_id: callId, action: 'review_request', details: { reason: reviewRequestReason }},
          user.id,
          user.name,
          user.role || 'user',
          'user'
        ).catch(console.log);
      }
      
      toast.success('Review request submitted! The QA team will review your call.');
      setShowReviewRequestDialog(false);
      setReviewRequestReason('');
      setReviewRequestExpected('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('already pending')) {
        toast.error('A review request for this call is already pending');
      } else {
        toast.error(detail || 'Failed to submit review request');
      }
    } finally {
      setIsSubmittingReviewRequest(false);
    }
  };
  
  // Handle Sync Call - re-match call to users based on current config
  const handleSyncCall = async () => {
    setIsSyncing(true);
    try {
      const { data } = await syncSingleCall(callId);
      if (data.matched) {
        toast.success(data.message);
        await fetchCall();  // Refresh call data
      } else {
        toast.info(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync call');
    } finally {
      setIsSyncing(false);
    }
  };
  
  // Load available users for assignment dialog
  const loadAvailableUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const { data } = await getUsers();
      setAvailableUsers(data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };
  
  // Handle Assign User
  const handleAssignUser = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    setIsAssigning(true);
    try {
      const { data } = await assignCallToUser(callId, selectedUserId);
      toast.success(data.message);
      setShowAssignUserDialog(false);
      setSelectedUserId('');
      await fetchCall();  // Refresh call data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    } finally {
      setIsAssigning(false);
    }
  };
  
  // Handle Edit Format - update call metadata
  const handleUpdateFormat = async () => {
    setIsUpdatingFormat(true);
    try {
      const updateData = {};
      if (editPhoneNumber) updateData.phone_number = editPhoneNumber;
      if (editExtension) updateData.extension = editExtension;
      if (editCallType) updateData.call_type = editCallType;
      
      if (Object.keys(updateData).length === 0) {
        toast.error('Please enter at least one field to update');
        setIsUpdatingFormat(false);
        return;
      }
      
      const { data } = await updateCallFormat(callId, updateData);
      toast.success(data.message);
      setShowEditFormatDialog(false);
      setEditPhoneNumber('');
      setEditExtension('');
      setEditCallType('');
      await fetchCall();  // Refresh call data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update call format');
    } finally {
      setIsUpdatingFormat(false);
    }
  };
  
  // Open assign user dialog
  const openAssignUserDialog = () => {
    loadAvailableUsers();
    setShowAssignUserDialog(true);
  };
  
  // Open edit format dialog with current values
  const openEditFormatDialog = () => {
    setEditPhoneNumber(call?.phone_number || '');
    setEditExtension(call?.extension || '');
    setEditCallType(call?.call_type || '');
    setShowEditFormatDialog(true);
  };

  // Initialize collaboration scores when dialog opens
  useEffect(() => {
    if (showCollaborateDialog && call) {
      const scores = call.quality_scores || {};
      setCollaborationScores(scores);
    }
  }, [showCollaborateDialog, call]);

  const formatDuration = (seconds) => {
    // Round to nearest second for cleaner display
    const totalSecs = Math.round(seconds);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };

  const NOTE_TYPE_CONFIG = {
    general: { label: 'General', color: 'bg-muted text-slate-700', icon: MessageSquare },
    feedback: { label: 'Feedback', color: 'bg-blue-100 text-blue-700', icon: MessageSquare },
    action_required: { label: 'Action Required', color: 'bg-red-100 text-red-700', icon: AlertCircle },
    positive: { label: 'Positive', color: 'bg-green-100 text-green-700', icon: ThumbsUp },
    improvement: { label: 'Needs Improvement', color: 'bg-amber-100 text-amber-700', icon: TrendingUp }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-32" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!call) {
    return (
      <div className="text-center py-12" data-testid="call-not-found">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Phone className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold mb-2">Call Not Found</h2>
        <p className="text-muted-foreground mb-4">
          This call may have been deleted or you may not have permission to view it.
        </p>
        <Link to={baseUrl}>
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
        </Link>
      </div>
    );
  }

  // Get scores from multiple possible locations (direct fields or nested in analysis)
  const scores = call.score_breakdown || call.quality_scores || call.analysis?.quality_scores || {};
  
  // Map of possible score keys (AI analysis uses these keys)
  // Includes new staffing-specific parameters
  const allScoreItems = [
    { key: 'communication_clarity', label: 'Communication Clarity' },
    { key: 'tone_professionalism', label: 'Tone & Professionalism' },
    { key: 'energy_confidence', label: 'Energy & Confidence' },
    { key: 'listening_skills', label: 'Listening Skills' },
    { key: 'rapport_building', label: 'Rapport Building' },
    { key: 'structure_flow', label: 'Structure & Flow' },
    { key: 'question_quality', label: 'Question Quality' },
    { key: 'candidate_engagement', label: 'Candidate Engagement' },
    { key: 'compliance_disclaimers', label: 'Compliance & Disclaimers' },
    { key: 'branding_compliance', label: 'Company Branding' },
    { key: 'rtr_compliance', label: 'RTR Compliance' },
    { key: 'job_knowledge', label: 'Job Knowledge' },
    { key: 'prescreening_completeness', label: 'Pre-Screening Completeness' },
    { key: 'objection_handling', label: 'Objection Handling' },
    { key: 'interview_coaching_quality', label: 'Interview Coaching Quality' },
    { key: 'offer_presentation', label: 'Offer Presentation' },
    { key: 'overall_effectiveness', label: 'Overall Effectiveness' },
    // Legacy keys for backwards compatibility
    { key: 'company_branding', label: 'Company Branding (Legacy)' },
    { key: 'communication_skills', label: 'Communication Skills' },
    { key: 'call_structure', label: 'Call Structure' },
    { key: 'active_listening', label: 'Active Listening' },
    { key: 'professionalism', label: 'Professionalism' },
    { key: 'closing_techniques', label: 'Closing Techniques' },
    { key: 'product_knowledge', label: 'Product Knowledge' },
  ];
  
  // Filter to show only scores that exist and are not "N/A"
  const scoreItems = allScoreItems.filter(item => {
    const value = scores[item.key];
    return value !== undefined && value !== "N/A" && value !== null;
  });
  
  // Get N/A scores to show separately
  const naScores = allScoreItems.filter(item => {
    const value = scores[item.key];
    return value === "N/A";
  });

  return (
    <div className="space-y-6" data-testid="call-detail">
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <Link to={backUrl}>
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Button>
        </Link>
        
        {/* Previous/Next Call Navigation */}
        <div className="flex items-center gap-2">
          {siblingCalls.length > 1 && (
            <>
              <span className="text-sm text-muted-foreground">
                Call {currentCallIndex + 1} of {siblingCalls.length}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousCall}
                disabled={currentCallIndex <= 0 || isLoadingSiblings}
                className="gap-1"
                data-testid="prev-call-btn"
              >
                <ChevronUp className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextCall}
                disabled={currentCallIndex >= siblingCalls.length - 1 || isLoadingSiblings}
                className="gap-1"
                data-testid="next-call-btn"
              >
                Next
                <ChevronDown className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header */}
      <div className={`
        relative overflow-hidden rounded-lg p-6
        ${call.alert_level === 'critical' ? 'bg-red-900 tracing-beam' :
          call.alert_level === 'medium' ? 'bg-amber-900' :
          'bg-slate-900'}
        text-white
      `}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative flex flex-col md:flex-row md:items-start justify-between gap-6">
          {/* Left Side - Call Info */}
          <div className="flex-1">
            <div className="flex items-start gap-4">
              {/* Call Type Badge */}
              {(() => {
                const typeInfo = getCallTypeInfo(call.call_type || call.detected_call_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div className={`p-3 rounded-xl ${typeInfo.bg}`}>
                    <TypeIcon className={`h-8 w-8 ${typeInfo.color}`} />
                  </div>
                );
              })()}
              
              <div className="flex-1">
                {/* Call Type Label */}
                <div className="flex items-center gap-3 mb-2">
                  {(() => {
                    const typeInfo = getCallTypeInfo(call.call_type || call.detected_call_type);
                    return (
                      <Badge className={`${typeInfo.bg} ${typeInfo.color} border-0 text-sm font-medium`}>
                        {typeInfo.label}
                      </Badge>
                    );
                  })()}
                  {call.format_used && (
                    <Badge variant="outline" className="text-white/70 border-white/30 text-xs">
                      Format: {call.format_used}
                    </Badge>
                  )}
                  {call.scoring_config_name && (
                    <Badge variant="outline" className="text-white/70 border-white/30 text-xs flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {call.scoring_config_name}
                    </Badge>
                  )}
                  {call.source && (
                    <Badge variant="outline" className="text-white/70 border-white/30 text-xs capitalize">
                      {call.source === 'manual_upload' ? 'Manual Upload' : call.source}
                    </Badge>
                  )}
                </div>
                
                <h1 className="text-xl font-bold font-heading mb-1">{call.filename}</h1>
                
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/60">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDuration(call.duration)}
                  </span>
                  {call.phone_number && (
                    <button
                      onClick={() => relatedCalls.length > 0 && setShowRelatedCallsDialog(true)}
                      className={`font-mono flex items-center gap-1 ${relatedCalls.length > 0 ? 'hover:text-amber-400 cursor-pointer transition-colors' : 'cursor-default'}`}
                      data-testid="filename-phone-btn"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      <span>{call.phone_number}</span>
                      {relatedCalls.length > 0 && (
                        <Badge variant="secondary" className="ml-1 bg-amber-500/20 text-amber-400 text-[10px] px-1 py-0">
                          {relatedCalls.length}
                        </Badge>
                      )}
                    </button>
                  )}
                  {call.call_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {format(new Date(call.call_date), 'MMM dd, yyyy h:mm a')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Assigned User Card - Compact Layout */}
            <div className="mt-3 p-3 bg-white/10 rounded-lg backdrop-blur-sm">
              {/* Top Row: User + Auditor */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* Left: Assigned User - Clickable */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {assignedUser || call.user ? (
                    <button
                      onClick={handleUserClick}
                      className="flex items-center gap-3 flex-1 min-w-0 p-1.5 -m-1.5 rounded-lg hover:bg-white/5 transition-colors cursor-pointer text-left"
                      data-testid="assigned-user-btn"
                    >
                      <Avatar className="h-10 w-10 border-2 border-emerald-500/30 flex-shrink-0">
                        <AvatarImage src={getAvatarDisplayUrl(assignedUser || call.user)} />
                        <AvatarFallback className="bg-emerald-700 text-sm text-white">
                          {(assignedUser || call.user)?.name?.charAt(0) || <User className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <UserCheck className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                          <span className="text-[10px] text-white/50 uppercase tracking-wide">Assigned</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <p className="font-medium text-white text-sm truncate">
                            {(assignedUser || call.user)?.name || 'Unknown User'}
                          </p>
                          <ChevronDown className="h-3 w-3 text-white/40 flex-shrink-0" />
                        </div>
                        <p className="text-xs text-white/50 truncate">
                          {(assignedUser || call.user)?.email}
                          {(assignedUser || call.user)?.extension && ` • Ext: ${(assignedUser || call.user).extension}`}
                        </p>
                      </div>
                      {(assignedUser || call.user)?.department_name && (
                        <Badge variant="outline" className="text-white/60 border-white/20 text-[10px] hidden sm:flex">
                          {(assignedUser || call.user).department_name}
                        </Badge>
                      )}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 text-white/50">
                      <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                        <User className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-[10px] text-white/40 uppercase">Not Assigned</p>
                        <p className="text-xs">Call not synced</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="hidden sm:block w-px h-10 bg-white/10" />

                {/* Right: Quality Auditor */}
                <div className="flex items-center gap-2">
                  <Shield className="h-3.5 w-3.5 text-blue-400 flex-shrink-0" />
                  <span className="text-[10px] text-white/50 uppercase">Auditor:</span>
                  {assignedAuditor ? (
                    <button
                      onClick={handleAuditorClick}
                      className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-500/20 hover:bg-blue-500/30 transition-colors"
                      data-testid="auditor-name-btn"
                    >
                      <span className="font-medium text-blue-300 text-sm">
                        {assignedAuditor.name || assignedAuditor.full_name || 'Unknown'}
                      </span>
                      <ChevronDown className="h-3 w-3 text-blue-400" />
                    </button>
                  ) : (
                    <span className="text-white/40 text-sm">NA</span>
                  )}
                </div>
              </div>
              
              {/* User Popup Card - Moved inside card but will render fixed */}
              
              {/* Auditor Popup Card - Moved inside card but will render fixed */}
              
              {/* Action Buttons - Compact row */}
              <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-2.5 border-t border-white/10">
                {/* Reprocess Button */}
                {canReprocess && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openReprocessDialog}
                    disabled={isReprocessing}
                    className="h-7 text-xs bg-white/10 hover:bg-white/20 text-white border-white/20 px-2"
                    data-testid="reprocess-call-btn"
                  >
                    {isReprocessing ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        {reprocessStatus === 'starting' ? 'Starting...' : 
                         reprocessStatus === 'processing' ? 'Processing...' : 'Reprocessing...'}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Reprocess
                      </>
                    )}
                  </Button>
                )}
                
                {/* Regenerate Transcript Button */}
                {canReprocess && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleRegenerateTranscript}
                    disabled={isRegeneratingTranscript || isReprocessing}
                    className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-white border-amber-400/30 px-2"
                    data-testid="regenerate-transcript-btn"
                    title="Re-transcribe the audio with improved speaker identification"
                  >
                    {isRegeneratingTranscript ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <FileAudio className="h-3 w-3 mr-1" />
                        Regenerate Transcript
                      </>
                    )}
                  </Button>
                )}
                
                {/* Collaborate Button */}
                {canCreateNotes && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowCollaborateDialog(true)}
                    className="h-7 text-xs bg-purple-500/20 hover:bg-purple-500/30 text-white border-purple-400/30 px-2"
                  >
                    <Handshake className="h-3 w-3 mr-1" />
                    Collaborate
                  </Button>
                )}
                
                {/* Sync Call Button */}
                {canCreateNotes && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleSyncCall}
                    disabled={isSyncing}
                    className="h-7 text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-white border-cyan-400/30 px-2"
                    title="Re-sync call to match with user based on phone number/extension"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Link2 className="h-3 w-3 mr-1" />
                        Sync
                      </>
                    )}
                  </Button>
                )}
                
                {/* Assign User Button */}
                {canCreateNotes && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openAssignUserDialog}
                    className="h-7 text-xs bg-green-500/20 hover:bg-green-500/30 text-white border-green-400/30 px-2"
                    title="Manually assign this call to a user"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    {call?.user_id ? 'Reassign' : 'Assign'}
                  </Button>
                )}
                
                {/* Edit Format Button */}
                {canCreateNotes && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openEditFormatDialog}
                    className="h-7 text-xs bg-amber-500/20 hover:bg-amber-500/30 text-white border-amber-400/30 px-2"
                    title="Edit call metadata (phone number, extension, call type)"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
                
                {/* Request Review Button (for users) */}
                {isUserCall && !canCreateNotes && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowReviewRequestDialog(true)}
                    className="h-7 text-xs bg-blue-500/20 hover:bg-blue-500/30 text-white border-blue-400/30 px-2"
                  >
                    <Send className="h-3 w-3 mr-1" />
                    Request Review
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Side - Score & Status */}
          <div className="flex flex-col items-end gap-4">
            {/* Score */}
            <div className="text-center bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-center gap-2 justify-center">
                <Star className="h-6 w-6 text-amber-400" />
                <span className="text-4xl font-bold">{call.average_score || '-'}</span>
                <span className="text-lg text-white/60">/10</span>
              </div>
              <p className="text-sm text-white/60 mt-1">Average Score</p>
              
              {/* Phone Number with Related Calls - Compact inline display */}
              {candidatePhoneNumber && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <button
                    onClick={() => relatedCalls.length > 0 && setShowRelatedCallsDialog(true)}
                    className={`text-sm flex items-center gap-2 justify-center ${relatedCalls.length > 0 ? 'hover:text-amber-400 cursor-pointer transition-colors' : 'cursor-default'}`}
                    disabled={relatedCalls.length === 0}
                    data-testid="header-phone-related-btn"
                  >
                    <Phone className="h-3.5 w-3.5 text-white/60" />
                    <span className="font-mono text-white/80">{candidatePhoneNumber}</span>
                    {relatedCalls.length > 0 && (
                      <>
                        <span className="text-white/40">-</span>
                        <span className="text-amber-400 font-semibold">{relatedCalls.length}</span>
                      </>
                    )}
                  </button>
                  {relatedCalls.length > 0 && (
                    <p className="text-xs text-white/40 mt-1">Related Calls</p>
                  )}
                  {isLoadingRelatedCalls && (
                    <div className="flex items-center gap-1 justify-center mt-1">
                      <Loader2 className="h-3 w-3 animate-spin text-white/40" />
                      <span className="text-xs text-white/40">Checking...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Badges - Improved light theme visibility */}
            <div className="flex flex-col gap-2">
              <Badge className={`classification-${call.classification} capitalize text-sm px-3 py-1 border border-white/20`}>
                {call.classification?.replace('_', ' ') || 'Unclassified'}
              </Badge>
              <Badge className={`status-${call.alert_level} capitalize border border-white/20`}>
                {call.alert_level === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
                {call.alert_level || 'normal'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Continuation Call Banner */}
      {call.is_continuation_call && (
        <div className="bg-blue-500/10 dark:bg-blue-500/20 border border-blue-400 dark:border-blue-600 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg">
              <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-blue-700 dark:text-blue-300">Continuation Call</h3>
                <Badge variant="outline" className="border-blue-400 text-blue-700 dark:text-blue-300 text-xs">
                  {call.continuation_reason === 'voicemail_followup' && 'Voicemail Follow-up'}
                  {call.continuation_reason === 'disconnected_retry' && 'Disconnected Retry'}
                  {call.continuation_reason === 'same_day_callback' && 'Same-Day Callback'}
                  {call.continuation_reason === 'same_day_followup' && 'Same-Day Follow-up'}
                </Badge>
              </div>
              <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">
                This is a follow-up call from the same recruiter to the same candidate on the same day.
                {call.branding_exempt && ' Branding was not required (covered in previous call).'}
                {call.disclaimer_exempt && ' Disclaimer was not required (covered in previous call).'}
              </p>
              
              {/* Previous Call Reference */}
              {call.previous_call_summary && (
                <div className="bg-blue-500/10 dark:bg-blue-500/20 rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-1">
                    <ArrowRight className="h-3 w-3" />
                    Previous Call Reference
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>
                      <span className="text-blue-600/70 dark:text-blue-400/70">Time:</span>
                      <span className="ml-1 text-blue-700 dark:text-blue-300">
                        {call.previous_call_summary.call_date ? format(new Date(call.previous_call_summary.call_date), 'h:mm a') : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-600/70 dark:text-blue-400/70">Type:</span>
                      <span className="ml-1 text-blue-700 dark:text-blue-300">
                        {call.previous_call_summary.call_type?.replace(/_/g, ' ') || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-600/70 dark:text-blue-400/70">Duration:</span>
                      <span className="ml-1 text-blue-700 dark:text-blue-300">
                        {call.previous_call_summary.duration_seconds ? `${Math.round(call.previous_call_summary.duration_seconds)}s` : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-blue-600/70 dark:text-blue-400/70">Score:</span>
                      <span className="ml-1 text-blue-700 dark:text-blue-300">
                        {call.previous_call_summary.average_score ? `${call.previous_call_summary.average_score.toFixed(1)}/10` : 'N/A'}
                      </span>
                    </div>
                  </div>
                  {call.continuation_of_call_id && (
                    <Link
                      to={`${baseUrl}/call/${call.continuation_of_call_id}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2"
                    >
                      <Link2 className="h-3 w-3" />
                      View Previous Call
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audio Player */}
      <AudioPlayer 
        callId={callId} 
        audioUrl={call.audio_url}
        user={user}
        callFilename={call.filename}
        onAudioPlay={() => {
          if (user?.id && user?.name) {
            logCallAudit(
              { call_id: callId, action: 'audio_played', details: {} },
              user.id,
              user.name,
              user.role || 'user',
              isAdmin ? 'admin' : 'user'
            ).catch(console.log);
          }
        }}
      />

      {/* Tabs */}
      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary" className="gap-2" data-testid="summary-tab">
            <BookOpen className="h-4 w-4" />
            Summary
          </TabsTrigger>
          <TabsTrigger value="scores" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Scores
          </TabsTrigger>
          <TabsTrigger value="transcript" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            Transcript
          </TabsTrigger>
          <TabsTrigger value="feedback" className="gap-2">
            <FileText className="h-4 w-4" />
            Feedback
          </TabsTrigger>
          <TabsTrigger value="qa-notes" className="gap-2" data-testid="qa-notes-tab">
            <ClipboardList className="h-4 w-4" />
            QA Notes
            {qaNotes.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs bg-purple-100 text-purple-700">
                {qaNotes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="audit-trail" className="gap-2" data-testid="audit-trail-tab">
            <Shield className="h-4 w-4" />
            Audit Trail
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab - ENHANCED */}
        <TabsContent value="summary" className="mt-6" data-testid="summary-tab-content">
          <div className="space-y-6">
            {/* Executive Summary */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                  Call Summary
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {call.call_purpose || call.analysis?.call_purpose || 'Comprehensive overview of the call'}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Executive Summary Text */}
                <div className="prose prose-slate max-w-none p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line">
                    {call.call_summary?.executive_summary || 
                     call.analysis?.call_summary?.executive_summary || 
                     (typeof call.ai_feedback === 'object' ? call.ai_feedback?.executive_assessment : call.ai_feedback) ||
                     call.analysis?.ai_feedback?.executive_assessment ||
                     'No summary available. Reprocess this call to generate a detailed summary.'}
                  </p>
                </div>

                {/* Performance Overview - NEW */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    Performance Overview
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Top Strengths */}
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-2 flex items-center gap-1">
                        <ThumbsUp className="h-3 w-3" />
                        Top Strengths
                      </p>
                      <ul className="space-y-1 text-sm text-emerald-600 dark:text-emerald-400">
                        {scoreItems
                          .filter(item => (scores[item.key] || 0) >= 8)
                          .slice(0, 3)
                          .map(item => (
                            <li key={item.key} className="flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              {item.label}
                            </li>
                          ))}
                        {scoreItems.filter(item => (scores[item.key] || 0) >= 8).length === 0 && (
                          <li className="text-muted-foreground italic">No standout areas yet</li>
                        )}
                      </ul>
                    </div>
                    
                    {/* Areas for Improvement */}
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />
                        Focus Areas
                      </p>
                      <ul className="space-y-1 text-sm text-amber-600 dark:text-amber-400">
                        {scoreItems
                          .filter(item => (scores[item.key] || 0) > 0 && (scores[item.key] || 0) < 6)
                          .slice(0, 3)
                          .map(item => (
                            <li key={item.key} className="flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {item.label}
                            </li>
                          ))}
                        {scoreItems.filter(item => (scores[item.key] || 0) > 0 && (scores[item.key] || 0) < 6).length === 0 && (
                          <li className="text-muted-foreground italic">No major concerns</li>
                        )}
                      </ul>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-1">
                        <BarChart3 className="h-3 w-3" />
                        Score Distribution
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between text-emerald-600">
                          <span>High (8+)</span>
                          <span className="font-semibold">{scoreItems.filter(item => (scores[item.key] || 0) >= 8).length}</span>
                        </div>
                        <div className="flex justify-between text-blue-600">
                          <span>Medium (6-7)</span>
                          <span className="font-semibold">{scoreItems.filter(item => (scores[item.key] || 0) >= 6 && (scores[item.key] || 0) < 8).length}</span>
                        </div>
                        <div className="flex justify-between text-amber-600">
                          <span>Low (&lt;6)</span>
                          <span className="font-semibold">{scoreItems.filter(item => (scores[item.key] || 0) > 0 && (scores[item.key] || 0) < 6).length}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Discussion Points */}
                {(call.call_summary?.key_discussion_points?.length > 0 || call.analysis?.call_summary?.key_discussion_points?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-blue-500" />
                      Key Discussion Points
                    </h4>
                    <ul className="space-y-2">
                      {(call.call_summary?.key_discussion_points || call.analysis?.call_summary?.key_discussion_points || []).map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-600 dark:text-slate-400">
                          <ChevronRight className="h-4 w-4 text-blue-400 mt-0.5 shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Candidate Background */}
                {(call.call_summary?.candidate_background_mentioned || call.analysis?.call_summary?.candidate_background_mentioned) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 flex items-center gap-2">
                      <User className="h-4 w-4 text-emerald-500" />
                      Candidate Background
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      {call.call_summary?.candidate_background_mentioned || call.analysis?.call_summary?.candidate_background_mentioned}
                    </p>
                  </div>
                )}

                {/* Role Details */}
                {(call.call_summary?.role_details_discussed || call.analysis?.call_summary?.role_details_discussed) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-purple-500" />
                      Role Details Discussed
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      {call.call_summary?.role_details_discussed || call.analysis?.call_summary?.role_details_discussed}
                    </p>
                  </div>
                )}

                {/* Call Outcome & Follow-up */}
                <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      Call Outcome
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400">
                      {call.call_summary?.call_outcome || call.analysis?.call_summary?.call_outcome || call.call_outcome || 'Not specified'}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-2 flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-500" />
                      Follow-up Required
                    </h4>
                    <p className="text-slate-600 dark:text-slate-400">
                      {call.call_summary?.follow_up_required || call.analysis?.call_summary?.follow_up_required || 'No follow-up specified'}
                    </p>
                  </div>
                </div>

                {/* Concerns/Red Flags */}
                {(call.call_summary?.concerns_or_objections?.length > 0 || call.analysis?.call_summary?.concerns_or_objections?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      Concerns / Objections Raised
                    </h4>
                    <ul className="space-y-2">
                      {(call.call_summary?.concerns_or_objections || call.analysis?.call_summary?.concerns_or_objections || []).map((concern, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                          <span>{concern}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TA Leader Insights */}
            {(call.ta_leader_insights || call.analysis?.ta_leader_insights) && (
              <Card className="card-elevated border-indigo-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-indigo-700">
                    <UserCheck className="h-5 w-5" />
                    TA Leader Insights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Submission Recommendation */}
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
                    <div className={`p-3 rounded-full ${
                      (call.ta_leader_insights?.would_submit_candidate || call.analysis?.ta_leader_insights?.would_submit_candidate)
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {(call.ta_leader_insights?.would_submit_candidate || call.analysis?.ta_leader_insights?.would_submit_candidate)
                        ? <ThumbsUp className="h-6 w-6" />
                        : <AlertCircle className="h-6 w-6" />
                      }
                    </div>
                    <div>
                      <h4 className="font-semibold">
                        {(call.ta_leader_insights?.would_submit_candidate || call.analysis?.ta_leader_insights?.would_submit_candidate)
                          ? 'Recommended for Submission'
                          : 'Not Recommended for Submission'
                        }
                      </h4>
                      <p className="text-sm text-slate-600">
                        {call.ta_leader_insights?.submission_reasoning || call.analysis?.ta_leader_insights?.submission_reasoning}
                      </p>
                    </div>
                    <div className="ml-auto text-right">
                      <span className={`text-2xl font-bold ${
                        (call.ta_leader_insights?.overall_call_grade || call.analysis?.ta_leader_insights?.overall_call_grade)?.startsWith('A') ? 'text-emerald-600' :
                        (call.ta_leader_insights?.overall_call_grade || call.analysis?.ta_leader_insights?.overall_call_grade)?.startsWith('B') ? 'text-blue-600' :
                        (call.ta_leader_insights?.overall_call_grade || call.analysis?.ta_leader_insights?.overall_call_grade)?.startsWith('C') ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {call.ta_leader_insights?.overall_call_grade || call.analysis?.ta_leader_insights?.overall_call_grade || 'N/A'}
                      </span>
                      <p className="text-xs text-slate-500">Overall Grade</p>
                    </div>
                  </div>

                  {/* Missed Information */}
                  {(call.ta_leader_insights?.missed_information?.length > 0 || call.analysis?.ta_leader_insights?.missed_information?.length > 0) && (
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-2">Information Missed</h4>
                      <div className="flex flex-wrap gap-2">
                        {(call.ta_leader_insights?.missed_information || call.analysis?.ta_leader_insights?.missed_information || []).map((item, idx) => (
                          <span key={idx} className="px-2 py-1 text-sm bg-amber-50 text-amber-700 rounded border border-amber-200">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Priority Improvement */}
                  {(call.ta_leader_insights?.call_improvement_priority || call.analysis?.ta_leader_insights?.call_improvement_priority) && (
                    <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <h4 className="font-semibold text-indigo-800 mb-1">Priority Improvement</h4>
                      <p className="text-indigo-700">
                        {call.ta_leader_insights?.call_improvement_priority || call.analysis?.ta_leader_insights?.call_improvement_priority}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Candidate Assessment */}
            {(call.candidate_assessment || call.analysis?.candidate_assessment) && (
              <Card className="card-elevated border-emerald-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-emerald-700">
                    <UserSearch className="h-5 w-5" />
                    Candidate Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Engagement & Answer Quality Scores */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-emerald-600">
                        {call.candidate_assessment?.engagement_level?.score || call.analysis?.candidate_assessment?.engagement_level?.score || 'N/A'}
                      </div>
                      <p className="text-sm text-slate-600">Engagement Score</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {call.candidate_assessment?.answer_quality?.score || call.analysis?.candidate_assessment?.answer_quality?.score || 'N/A'}
                      </div>
                      <p className="text-sm text-slate-600">Answer Quality</p>
                    </div>
                  </div>

                  {/* Fit Recommendation */}
                  {(call.candidate_assessment?.fit_recommendation || call.analysis?.candidate_assessment?.fit_recommendation) && (
                    <div className={`p-4 rounded-lg border ${
                      (call.candidate_assessment?.fit_recommendation?.verdict || call.analysis?.candidate_assessment?.fit_recommendation?.verdict) === 'Strong Candidate' 
                        ? 'bg-emerald-50 border-emerald-300' 
                        : (call.candidate_assessment?.fit_recommendation?.verdict || call.analysis?.candidate_assessment?.fit_recommendation?.verdict) === 'Good Candidate'
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-amber-50 border-amber-300'
                    }`}>
                      <h4 className="font-semibold mb-2">
                        {call.candidate_assessment?.fit_recommendation?.verdict || call.analysis?.candidate_assessment?.fit_recommendation?.verdict}
                      </h4>
                      <p className="text-sm text-slate-600 mb-2">
                        {call.candidate_assessment?.fit_recommendation?.reasoning || call.analysis?.candidate_assessment?.fit_recommendation?.reasoning}
                      </p>
                      {(call.candidate_assessment?.fit_recommendation?.notes_for_account_manager || call.analysis?.candidate_assessment?.fit_recommendation?.notes_for_account_manager) && (
                        <p className="text-sm text-slate-500 italic">
                          <strong>Note for AM:</strong> {call.candidate_assessment?.fit_recommendation?.notes_for_account_manager || call.analysis?.candidate_assessment?.fit_recommendation?.notes_for_account_manager}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Green & Red Flags */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(call.candidate_assessment?.engagement_level?.green_flags?.length > 0 || call.analysis?.candidate_assessment?.engagement_level?.green_flags?.length > 0) && (
                      <div>
                        <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" /> Green Flags
                        </h4>
                        <ul className="space-y-1">
                          {(call.candidate_assessment?.engagement_level?.green_flags || call.analysis?.candidate_assessment?.engagement_level?.green_flags || []).map((flag, idx) => (
                            <li key={idx} className="text-sm text-emerald-600 flex items-start gap-1">
                              <span>✓</span> {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(call.candidate_assessment?.engagement_level?.red_flags?.length > 0 || call.analysis?.candidate_assessment?.engagement_level?.red_flags?.length > 0) && (
                      <div>
                        <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" /> Red Flags
                        </h4>
                        <ul className="space-y-1">
                          {(call.candidate_assessment?.engagement_level?.red_flags || call.analysis?.candidate_assessment?.engagement_level?.red_flags || []).map((flag, idx) => (
                            <li key={idx} className="text-sm text-red-600 flex items-start gap-1">
                              <span>✗</span> {flag}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Scores Tab */}
        <TabsContent value="scores" className="mt-6">
          <div className="space-y-4">
            {/* Collaboration Status Banner */}
            {call.is_collaborated && (
              <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <Handshake className="h-5 w-5 text-purple-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300">
                    This call has been reviewed collaboratively
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-400">
                    Scores combine AI analysis with auditor expertise ({call.collaboration_count || 1} review{call.collaboration_count > 1 ? 's' : ''})
                  </p>
                </div>
              </div>
            )}
            
            {/* Three Column Layout: Score Breakdown | AI Evidence | Compliance Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Quality Score Breakdown */}
              <Card className="card-elevated flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-blue-600" />
                    Quality Score Breakdown
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Click scores for details</p>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto max-h-[500px] space-y-2">
                  {scoreItems.map(({ key, label }) => {
                    const score = scores[key] || 0;
                    const scoreDetails = call.score_details?.[key] || call.analysis?.score_details?.[key];
                    const deduction = 10 - score;
                    
                    return (
                      <Collapsible key={key} className="group">
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/50 dark:bg-slate-800/50 hover:bg-muted dark:hover:bg-slate-800 transition-colors cursor-pointer">
                            <div className="flex items-center gap-2 flex-1">
                              <div className={`w-2 h-2 rounded-full ${
                                score >= 8 ? 'bg-emerald-500' :
                                score >= 6 ? 'bg-blue-500' :
                                score >= 4 ? 'bg-amber-500' :
                                'bg-red-500'
                              }`} />
                              <span className="text-xs font-medium text-slate-700 dark:text-muted-foreground/70 truncate">{label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-16">
                                <Progress 
                                  value={score * 10} 
                                  className={`h-1.5 ${
                                    score >= 8 ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                                    score >= 6 ? 'bg-blue-100 dark:bg-blue-900/30' :
                                    score >= 4 ? 'bg-amber-100 dark:bg-amber-900/30' :
                                    'bg-red-100 dark:bg-red-900/30'
                                  }`} 
                                />
                              </div>
                              <span className={`text-xs font-bold min-w-[36px] text-right ${
                                score >= 8 ? 'text-emerald-600' :
                                score >= 6 ? 'text-blue-600' :
                                score >= 4 ? 'text-amber-600' :
                                'text-red-600'
                              }`}>
                                {score.toFixed ? score.toFixed(1) : score}
                              </span>
                              <ChevronRight className="h-3 w-3 text-muted-foreground group-data-[state=open]:rotate-90 transition-transform" />
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-3 mr-1 mt-1 p-3 rounded-lg bg-muted dark:bg-slate-800 border-l-2 border-l-slate-300 dark:border-l-slate-600 space-y-3 text-xs max-h-[400px] overflow-y-auto">
                            {/* 1. Summary of Score */}
                            <div className="p-2 bg-slate-100 dark:bg-slate-700/50 rounded">
                              <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-1 flex items-center gap-1">
                                <Star className="h-3 w-3 text-blue-500" />
                                Score Summary
                              </h4>
                              <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                {scoreDetails?.analysis || (
                                  score >= 8 ? `Excellent performance in ${label.toLowerCase()}. The recruiter demonstrated strong competency in this area.` :
                                  score >= 6 ? `Good performance in ${label.toLowerCase()} with some room for improvement. The recruiter met most expectations.` :
                                  score >= 4 ? `Moderate performance in ${label.toLowerCase()}. Several areas need attention for better results.` :
                                  `Significant improvement needed in ${label.toLowerCase()}. This is a critical area requiring immediate focus.`
                                )}
                              </p>
                            </div>

                            {/* 2. Analysis - What was observed */}
                            <div>
                              <h4 className="font-bold text-slate-800 dark:text-slate-200 mb-1 flex items-center gap-1">
                                <Search className="h-3 w-3 text-purple-500" />
                                Analysis
                              </h4>
                              {scoreDetails?.evidence ? (
                                <div className="text-slate-600 dark:text-slate-400 pl-2 border-l-2 border-purple-300 dark:border-purple-600 italic">
                                  "{scoreDetails.evidence}"
                                </div>
                              ) : (
                                <p className="text-slate-600 dark:text-slate-400">
                                  {score >= 6 ? 'The call demonstrated positive indicators in this parameter.' : 'Specific examples were observed that affected the score.'}
                                </p>
                              )}
                              
                              {/* What was good */}
                              {scoreDetails?.what_was_good && (
                                <div className="mt-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded">
                                  <p className="text-emerald-700 dark:text-emerald-300 font-medium mb-1">✓ What was done well:</p>
                                  <p className="text-emerald-600 dark:text-emerald-400 whitespace-pre-line">{scoreDetails.what_was_good}</p>
                                </div>
                              )}
                            </div>

                            {/* 3. Scope of Improvement with examples */}
                            <div className="p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                              <h4 className="font-bold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                Scope of Improvement
                              </h4>
                              
                              {scoreDetails?.what_needs_improvement ? (
                                <p className="text-amber-700 dark:text-amber-400 whitespace-pre-line mb-2">{scoreDetails.what_needs_improvement}</p>
                              ) : (
                                <p className="text-amber-700 dark:text-amber-400 mb-2">
                                  {score >= 8 ? 'Minor refinements could elevate this to perfect.' :
                                   score >= 6 ? 'Focus on consistency and adding more specific details.' :
                                   'Structured practice and specific techniques can help improve.'}
                                </p>
                              )}
                              
                              {/* Coaching Script / Examples */}
                              {scoreDetails?.coaching_script && (
                                <div className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border border-amber-300 dark:border-amber-700">
                                  <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">💡 Example to Practice:</p>
                                  <p className="text-amber-700 dark:text-amber-400 italic">"{scoreDetails.coaching_script}"</p>
                                </div>
                              )}
                              
                              {/* How to Improve */}
                              {scoreDetails?.how_to_improve && (
                                <div className="mt-2 p-2 bg-white dark:bg-slate-800 rounded border border-amber-300 dark:border-amber-700">
                                  <p className="font-medium text-amber-800 dark:text-amber-300 mb-1">📋 Action Steps:</p>
                                  <p className="text-amber-700 dark:text-amber-400 whitespace-pre-line">{scoreDetails.how_to_improve}</p>
                                </div>
                              )}
                            </div>

                            {/* 4. Why Points Were Deducted */}
                            {deduction > 0 && (
                              <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                                <h4 className="font-bold text-red-800 dark:text-red-300 mb-1 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  Why Points Were Deducted ({deduction.toFixed(1)} points)
                                </h4>
                                {scoreDetails?.points_deducted_reason ? (
                                  <p className="text-red-700 dark:text-red-400 whitespace-pre-line">{scoreDetails.points_deducted_reason}</p>
                                ) : (
                                  <p className="text-red-700 dark:text-red-400">
                                    {deduction >= 5 ? `Critical gaps in ${label.toLowerCase()} were identified. Focus on the improvement areas above to regain these points.` :
                                     deduction >= 3 ? `Some elements of ${label.toLowerCase()} were missing or incomplete.` :
                                     `Minor deductions for small inconsistencies. Close to full marks.`}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Points Awarded (for context) */}
                            {scoreDetails?.points_awarded_reason && (
                              <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded border border-emerald-200 dark:border-emerald-800">
                                <h4 className="font-bold text-emerald-800 dark:text-emerald-300 mb-1 flex items-center gap-1">
                                  <CheckCircle className="h-3 w-3" />
                                  Points Awarded ({score.toFixed(1)} points)
                                </h4>
                                <p className="text-emerald-700 dark:text-emerald-400 whitespace-pre-line">{scoreDetails.points_awarded_reason}</p>
                              </div>
                            )}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                  
                  {/* N/A Scores */}
                  {naScores.length > 0 && (
                    <div className="mt-3 pt-2 border-t border-slate-200">
                      <p className="text-xs text-muted-foreground mb-2">Not Applicable:</p>
                      <div className="flex flex-wrap gap-1">
                        {naScores.map(({ key, label }) => (
                          <Badge key={key} variant="outline" className="text-xs text-muted-foreground bg-muted/50">
                            {label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Analysis Evidence */}
              <Card className="card-elevated border-blue-200 flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2 text-blue-700">
                    <FileText className="h-5 w-5" />
                    AI Analysis Evidence
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto max-h-[500px] space-y-3">
                  {call.transcript_evidence && Object.keys(call.transcript_evidence).length > 0 ? (
                    <>
                      {/* Branding Check */}
                      <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                        <div className={`p-1 rounded-full ${call.transcript_evidence.branding_found ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {call.transcript_evidence.branding_found ? (
                            <CheckCircle className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <AlertCircle className="h-3 w-3 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-xs">Company Branding</p>
                          <p className="text-xs text-muted-foreground">
                            {call.transcript_evidence.branding_found ? 'Found' : 'Not Found'}
                          </p>
                          {call.transcript_evidence.branding_quote && (
                            <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">&quot;{call.transcript_evidence.branding_quote}&quot;</p>
                          )}
                        </div>
                      </div>

                      {/* Quality Disclaimer Check */}
                      <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                        <div className={`p-1 rounded-full ${call.transcript_evidence.disclaimer_found ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {call.transcript_evidence.disclaimer_found ? (
                            <CheckCircle className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <AlertCircle className="h-3 w-3 text-red-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-xs">Quality Disclaimer</p>
                          <p className="text-xs text-muted-foreground">
                            {call.transcript_evidence.disclaimer_found ? 'Found' : 'Not Found'}
                          </p>
                          {call.transcript_evidence.disclaimer_quote && (
                            <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">&quot;{call.transcript_evidence.disclaimer_quote}&quot;</p>
                          )}
                        </div>
                      </div>

                      {/* Probing Questions */}
                      <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                        <div className={`p-1 rounded-full ${(call.transcript_evidence.probing_questions_count || 0) >= 3 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                          {(call.transcript_evidence.probing_questions_count || 0) >= 3 ? (
                            <CheckCircle className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <AlertCircle className="h-3 w-3 text-amber-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-xs">Probing Questions</p>
                          <p className="text-xs text-muted-foreground">
                            {call.transcript_evidence.probing_questions_count || 0} asked (min 3)
                          </p>
                          {call.transcript_evidence.probing_questions_examples?.length > 0 && (
                            <ul className="text-xs text-muted-foreground mt-1 list-disc list-inside">
                              {call.transcript_evidence.probing_questions_examples.slice(0, 2).map((q, i) => (
                                <li key={i} className="italic line-clamp-1">{q}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* Engagement Check */}
                      {call.transcript_evidence.engagement_phrases && (
                        <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                          <div className={`p-1 rounded-full ${call.transcript_evidence.engagement_phrases.length >= 2 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            {call.transcript_evidence.engagement_phrases.length >= 2 ? (
                              <CheckCircle className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <AlertCircle className="h-3 w-3 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-xs">Engagement Phrases</p>
                            <p className="text-xs text-muted-foreground">
                              {call.transcript_evidence.engagement_phrases.length} found
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Visa Context */}
                      {call.transcript_evidence.visa_context_discussed !== undefined && (
                        <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                          <div className={`p-1 rounded-full ${call.transcript_evidence.visa_context_discussed ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                            {call.transcript_evidence.visa_context_discussed ? (
                              <CheckCircle className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <AlertCircle className="h-3 w-3 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-xs">Visa Context</p>
                            <p className="text-xs text-muted-foreground">
                              {call.transcript_evidence.visa_context_discussed ? 'Discussed' : 'Not Applicable'}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* RTR Discussed */}
                      {call.transcript_evidence.rtr_discussed !== undefined && (
                        <div className="flex items-start gap-2 p-2 bg-muted/50 rounded-lg">
                          <div className={`p-1 rounded-full ${call.transcript_evidence.rtr_discussed ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                            {call.transcript_evidence.rtr_discussed ? (
                              <CheckCircle className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <AlertCircle className="h-3 w-3 text-amber-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-xs">Right to Represent (RTR)</p>
                            <p className="text-xs text-muted-foreground">
                              {call.transcript_evidence.rtr_discussed ? 'Discussed' : 'Not Discussed'}
                            </p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No AI evidence available</p>
                      <p className="text-xs text-muted-foreground">Reprocess call to generate</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Compliance Alerts */}
              <Card className="card-elevated flex flex-col">
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Compliance Alerts
                    {call.compliance_alerts?.length > 0 && (
                      <Badge variant={call.compliance_alerts.some(a => !a.overridden && a.status !== 'compliant') ? "destructive" : "secondary"} className="text-xs">
                        {call.compliance_alerts.filter(a => !a.overridden && a.status !== 'compliant').length} active
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto max-h-[500px]">
                  {call.compliance_alerts?.length > 0 ? (
                    <div className="space-y-2">
                      {call.compliance_alerts.map((alert, idx) => {
                        const isOverridden = alert.overridden || alert.status === 'compliant';
                        
                        return (
                          <div 
                            key={idx} 
                            className={`p-2 rounded-sm border ${
                              isOverridden ? 'bg-emerald-50 border-emerald-200' :
                              alert.severity === 'critical' ? 'bg-red-50 border-red-200' :
                              alert.severity === 'medium' ? 'bg-amber-50 border-amber-200' :
                              'bg-yellow-50 border-yellow-200'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                {isOverridden ? (
                                  <CheckCircle className="h-3 w-3 mt-0.5 shrink-0 text-emerald-500" />
                                ) : (
                                  <AlertCircle className={`h-3 w-3 mt-0.5 shrink-0 ${
                                    alert.severity === 'critical' ? 'text-red-500' :
                                    alert.severity === 'medium' ? 'text-amber-500' :
                                    'text-yellow-500'
                                  }`} />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <p className="text-xs font-medium capitalize truncate">{alert.type?.replace(/_/g, ' ')}</p>
                                    <Badge variant={isOverridden ? 'default' : 'outline'} className={`text-[10px] h-4 ${
                                      isOverridden ? 'bg-emerald-500' : 
                                      alert.severity === 'critical' ? 'border-red-300 text-red-700' :
                                      alert.severity === 'medium' ? 'border-amber-300 text-amber-700' :
                                      'border-yellow-300 text-yellow-700'
                                    }`}>
                                      {isOverridden ? 'OK' : alert.severity || 'Low'}
                                    </Badge>
                                  </div>
                                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{alert.description}</p>
                                  {alert.override_reason && (
                                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                      <CheckCircle className="h-2 w-2" />
                                      {alert.override_reason}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {/* Override Button */}
                              {canCreateNotes && !isOverridden && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAlertForOverride({ ...alert, index: idx });
                                    setShowIndividualOverrideDialog(true);
                                  }}
                                  disabled={overridingAlertIndex === idx}
                                  className="shrink-0 text-[10px] h-6 px-1.5 text-emerald-700 hover:bg-emerald-50"
                                >
                                  {overridingAlertIndex === idx ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle className="h-3 w-3" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Bulk Override Button */}
                      {canCreateNotes && call.compliance_alerts?.some(a => !a.overridden && a.status !== 'compliant') && (
                        <div className="pt-2 border-t mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowOverrideDialog(true)}
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 text-xs h-7"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            Override All ({call.compliance_alerts?.filter(a => !a.overridden && a.status !== 'compliant').length})
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground font-medium">No compliance issues</p>
                      <p className="text-xs text-muted-foreground">All standards met</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        {/* Transcript Tab */}
        <TabsContent value="transcript" className="mt-6">
          <Card className="card-elevated">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading">Call Transcript</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {call.transcript ? (
                <TranscriptWithSearch transcript={call.transcript} />
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-muted-foreground/70 mx-auto mb-4" />
                  <p className="text-muted-foreground">No transcript available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="mt-6">
          <div className="space-y-6">
            {/* Expert Learning Report — coaching-first feedback (Feb 2026) */}
            {(() => {
              const elr = call.expert_learning_report || call.analysis?.expert_learning_report;
              const hasReport = elr && typeof elr === 'object' && Object.keys(elr).length > 0;
              const canRegenerate = isAdmin || user?.is_admin || user?.role === 'admin' || user?.role === 'owner';

              if (hasReport) {
                return (
                  <Card className="card-elevated">
                    <CardContent className="p-6">
                      {canRegenerate && (
                        <div className="flex justify-end mb-3">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="regenerate-expert-report-btn"
                            onClick={handleRegenerateExpertReport}
                            disabled={isRegeneratingReport}
                            className="gap-2"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            {isRegeneratingReport ? 'Regenerating…' : 'Regenerate Report'}
                          </Button>
                        </div>
                      )}
                      <ExpertLearningReport report={elr} />
                    </CardContent>
                  </Card>
                );
              }

              // Missing ELR — show a CTA for admins to generate it on demand
              if (!canRegenerate) return null;
              return (
                <Card
                  data-testid="expert-report-empty-state"
                  className="card-elevated border-2 border-dashed border-violet-200 dark:border-violet-900 bg-gradient-to-br from-violet-50/40 to-fuchsia-50/30 dark:from-violet-950/10 dark:to-fuchsia-950/10"
                >
                  <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-md shrink-0">
                        <Sparkles className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 dark:text-slate-100">
                          Generate an Expert Learning Report
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-400">
                          Turn this call into a coaching exercise — wins, misses, scripts, compliance audit and 5-min practice drills. Takes ~15s.
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid="generate-expert-report-btn"
                      onClick={handleRegenerateExpertReport}
                      disabled={isRegeneratingReport}
                      className="gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shrink-0"
                    >
                      <Sparkles className="h-4 w-4" />
                      {isRegeneratingReport ? 'Generating…' : 'Generate Report'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })()}

            {/* AI Feedback - Full Width */}
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Expert QA Assessment
                  </CardTitle>
                  {(typeof call.ai_feedback === 'object' && call.ai_feedback?.grade) && (
                    <span className={`text-2xl font-bold px-3 py-1 rounded ${
                      call.ai_feedback.grade?.startsWith('A') ? 'bg-emerald-100 text-emerald-700' :
                      call.ai_feedback.grade?.startsWith('B') ? 'bg-blue-100 text-blue-700' :
                      call.ai_feedback.grade?.startsWith('C') ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      Grade: {call.ai_feedback.grade}
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Executive Assessment */}
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                    {typeof call.ai_feedback === 'object' 
                      ? (call.ai_feedback?.executive_assessment || call.ai_feedback?.one_liner_verdict || JSON.stringify(call.ai_feedback))
                      : (call.ai_feedback || call.analysis?.ai_feedback?.executive_assessment || call.analysis?.ai_feedback || call.transcript_summary || 
                         (call.average_score && `This call scored ${call.average_score}/10 overall.`) ||
                         'No AI feedback available for this call.')}
                  </p>
                </div>
                
                {/* Grade Justification */}
                {(typeof call.ai_feedback === 'object' && call.ai_feedback?.grade_justification) && (
                  <div className="p-3 bg-slate-50 rounded-lg border">
                    <h4 className="font-semibold text-slate-700 mb-1">Grade Justification</h4>
                    <p className="text-sm text-slate-600">{call.ai_feedback.grade_justification}</p>
                  </div>
                )}
                
                {/* QA Audit Status */}
                {(typeof call.ai_feedback === 'object' && call.ai_feedback?.would_pass_qa_audit !== undefined) && (
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${
                    call.ai_feedback.would_pass_qa_audit ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    {call.ai_feedback.would_pass_qa_audit 
                      ? <CheckCircle className="h-5 w-5 text-emerald-600" />
                      : <XCircle className="h-5 w-5 text-red-600" />
                    }
                    <div>
                      <span className="font-semibold">
                        {call.ai_feedback.would_pass_qa_audit ? 'Would Pass QA Audit' : 'Would Not Pass QA Audit'}
                      </span>
                      {call.ai_feedback.qa_audit_notes && (
                        <p className="text-sm text-slate-600 mt-1">{call.ai_feedback.qa_audit_notes}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Strengths */}
              <Card className="card-elevated border-emerald-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-emerald-700">
                    <CheckCircle className="h-5 w-5" />
                    Strengths
                    {(() => {
                      const strengthsData = call.strengths?.items || call.strengths || call.analysis?.strengths?.items || call.analysis?.strengths || [];
                      const count = Array.isArray(strengthsData) ? strengthsData.length : 0;
                      return count > 0 && <span className="text-sm font-normal text-emerald-600">({count} identified)</span>;
                    })()}
                  </CardTitle>
                  {(call.strengths?.overall_strengths_summary || call.analysis?.strengths?.overall_strengths_summary) && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {call.strengths?.overall_strengths_summary || call.analysis?.strengths?.overall_strengths_summary}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {(() => {
                    const strengthsData = call.strengths?.items || call.strengths || call.analysis?.strengths?.items || call.analysis?.strengths || [];
                    const strengthsArray = Array.isArray(strengthsData) ? strengthsData : [];
                    return strengthsArray.length > 0 ? (
                      <ul className="space-y-4 max-h-[500px] overflow-y-auto">
                        {strengthsArray.map((strength, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-700 p-3 bg-emerald-50/50 rounded-lg">
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-1 shrink-0" />
                            <div className="flex-1">
                              {typeof strength === 'string' ? (
                                strength
                              ) : (
                                <>
                                  {strength.title && <div className="font-semibold text-emerald-800">{strength.title}</div>}
                                  <div className="font-medium">{strength.observation || strength.category || 'Strength identified'}</div>
                                  {strength.timestamp && <span className="text-xs text-muted-foreground">@ {strength.timestamp}</span>}
                                  {strength.quote && (
                                    <div className="text-sm text-muted-foreground italic mt-1 pl-3 border-l-2 border-emerald-300">"{strength.quote}"</div>
                                  )}
                                  {strength.impact && (
                                    <div className="text-sm text-emerald-600 mt-2"><strong>Why it matters:</strong> {strength.impact}</div>
                                  )}
                                  {strength.coaching_note && (
                                    <div className="text-xs text-emerald-700 mt-2 p-2 bg-emerald-100 rounded">💡 {strength.coaching_note}</div>
                                  )}
                                </>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground">No strengths identified</p>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Improvement Areas */}
              <Card className="card-elevated border-amber-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-amber-700">
                    <AlertCircle className="h-5 w-5" />
                    Areas for Improvement
                    {(() => {
                      const areasData = call.improvement_areas?.items || call.improvement_areas || call.analysis?.improvement_areas?.items || call.analysis?.improvement_areas || [];
                      const count = Array.isArray(areasData) ? areasData.length : 0;
                      return count > 0 && <span className="text-sm font-normal text-amber-600">({count} identified)</span>;
                    })()}
                  </CardTitle>
                  {(call.improvement_areas?.top_3_priorities?.length > 0 || call.analysis?.improvement_areas?.top_3_priorities?.length > 0) && (
                    <div className="mt-2">
                      <p className="text-xs font-semibold text-amber-800 mb-1">Top Priorities:</p>
                      <div className="flex flex-wrap gap-1">
                        {(call.improvement_areas?.top_3_priorities || call.analysis?.improvement_areas?.top_3_priorities || []).map((p, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">{i+1}. {p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {(() => {
                    const areasData = call.improvement_areas?.items || call.improvement_areas || call.analysis?.improvement_areas?.items || call.analysis?.improvement_areas || [];
                    const areasArray = Array.isArray(areasData) ? areasData : [];
                    return areasArray.length > 0 ? (
                      <ul className="space-y-4 max-h-[500px] overflow-y-auto">
                        {areasArray.map((area, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-slate-700 p-3 bg-amber-50/50 rounded-lg border-l-4 border-amber-400">
                            <div className="flex-1">
                              {typeof area === 'string' ? (
                                area
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 mb-1">
                                    {area.priority && <span className="px-2 py-0.5 text-xs font-bold bg-amber-200 text-amber-800 rounded">P{area.priority}</span>}
                                    {area.title && <span className="font-semibold text-amber-800">{area.title}</span>}
                                  </div>
                                  <div className="font-medium">{area.observation || area.category || 'Area for improvement'}</div>
                                  {area.timestamp && <span className="text-xs text-muted-foreground">@ {area.timestamp}</span>}
                                  {area.quote && (
                                    <div className="text-sm text-muted-foreground italic mt-2 pl-3 border-l-2 border-amber-300">
                                      <strong>What was said:</strong> "{area.quote}"
                                    </div>
                                  )}
                                  {area.why_this_matters && (
                                    <div className="text-sm text-amber-700 mt-2"><strong>Why it matters:</strong> {area.why_this_matters}</div>
                                  )}
                                  {area.better_approach && (
                                    <div className="text-sm text-blue-600 mt-2 p-2 bg-blue-50 rounded">
                                      <strong>Better approach:</strong> {area.better_approach}
                                    </div>
                                  )}
                                  {area.root_cause && (
                                    <div className="text-sm text-slate-600 mt-1"><strong>Root cause:</strong> {area.root_cause}</div>
                                  )}
                                  {area.practice_exercise && (
                                    <div className="text-xs text-muted-foreground mt-2 p-2 bg-slate-50 rounded">📝 <strong>Practice:</strong> {area.practice_exercise}</div>
                                  )}
                                </>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted-foreground">No improvement areas identified</p>
                    );
                  })()}
                  {(call.improvement_areas?.overall_improvement_summary || call.analysis?.improvement_areas?.overall_improvement_summary) && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-1">Overall Summary</h4>
                      <p className="text-sm text-amber-700">{call.improvement_areas?.overall_improvement_summary || call.analysis?.improvement_areas?.overall_improvement_summary}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Coaching Tips - Full Width */}
            <Card className="card-elevated border-blue-200">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-blue-700">
                  <Star className="h-5 w-5" />
                  Coaching Tips & Development Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Immediate Actions */}
                {(() => {
                  const tipsData = call.coaching_tips?.immediate_actions || call.coaching_tips || call.analysis?.coaching_tips?.immediate_actions || call.analysis?.coaching_tips || [];
                  const tipsArray = Array.isArray(tipsData) ? tipsData : [];
                  return tipsArray.length > 0 ? (
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-3">Immediate Actions</h4>
                      <ul className="space-y-4">
                        {tipsArray.map((tip, idx) => (
                          <li key={idx} className="p-4 bg-blue-50/50 rounded-lg border-l-4 border-blue-400">
                            {typeof tip === 'string' ? (
                              <div className="flex items-start gap-2">
                                <Star className="h-4 w-4 text-blue-500 mt-1 shrink-0" />
                                <span>{tip}</span>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 mb-2">
                                  {tip.priority && <span className="px-2 py-0.5 text-xs font-bold bg-blue-200 text-blue-800 rounded">Priority {tip.priority}</span>}
                                  <span className="font-semibold">{tip.tip || tip.suggestion || tip.observation || 'Coaching Tip'}</span>
                                </div>
                                {tip.based_on && (
                                  <div className="text-sm text-slate-600 mb-2">
                                    <strong>Based on:</strong> {tip.based_on}
                                  </div>
                                )}
                                {tip.current_behavior && (
                                  <div className="text-sm text-amber-700 mb-1">
                                    <strong>Current:</strong> {tip.current_behavior}
                                  </div>
                                )}
                                {tip.target_behavior && (
                                  <div className="text-sm text-emerald-700 mb-1">
                                    <strong>Target:</strong> {tip.target_behavior}
                                  </div>
                                )}
                                {tip.practice_script && (
                                  <div className="text-sm p-2 bg-white rounded border border-blue-200 mt-2">
                                    <strong>Practice Script:</strong>
                                    <div className="italic text-blue-700 mt-1">"{tip.practice_script}"</div>
                                  </div>
                                )}
                                {tip.expected_outcome && (
                                  <div className="text-xs text-muted-foreground mt-2">
                                    <strong>Expected outcome:</strong> {tip.expected_outcome}
                                  </div>
                                )}
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No coaching tips available</p>
                  );
                })()}

                {/* Long-term Development */}
                {(call.coaching_tips?.long_term_development?.length > 0 || call.analysis?.coaching_tips?.long_term_development?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Long-term Development Areas</h4>
                    <ul className="space-y-2">
                      {(call.coaching_tips?.long_term_development || call.analysis?.coaching_tips?.long_term_development || []).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-600">
                          <TrendingUp className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommended Training */}
                {(call.coaching_tips?.recommended_training?.length > 0 || call.analysis?.coaching_tips?.recommended_training?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Recommended Training</h4>
                    <div className="flex flex-wrap gap-2">
                      {(call.coaching_tips?.recommended_training || call.analysis?.coaching_tips?.recommended_training || []).map((item, idx) => (
                        <span key={idx} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-full">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mentor Discussion Points */}
                {(call.coaching_tips?.mentor_discussion_points?.length > 0 || call.analysis?.coaching_tips?.mentor_discussion_points?.length > 0) && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Points to Discuss with Mentor/Lead</h4>
                    <ul className="space-y-1">
                      {(call.coaching_tips?.mentor_discussion_points || call.analysis?.coaching_tips?.mentor_discussion_points || []).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                          <Handshake className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Fatal Parameter Analysis - Full Width Section */}
          {(call.fatal_parameter_analysis || call.analysis?.fatal_parameter_analysis) && (
            <Card className="card-elevated border-red-200 mt-6" data-testid="fatal-parameter-analysis">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  Fatal Parameter Analysis
                </CardTitle>
                <p className="text-sm text-muted-foreground">Detailed reasoning for each fatal parameter evaluation</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(call.fatal_parameter_analysis || call.analysis?.fatal_parameter_analysis || {}).map(([key, analysis]) => {
                    if (key === 'NOTE' || !analysis || typeof analysis !== 'object') return null;
                    const isViolation = analysis.violation_occurred === true;
                    const parameterLabels = {
                      branding: 'Introduction with Branding',
                      quality_disclaimer: 'Quality Disclaimer',
                      visa_term_usage: 'Using the Term "Visa"',
                      dob_ssn_request: 'DOB/SSN Request',
                      unprofessional_communication: 'Unprofessional Communication',
                      probing_questions: 'Open-Ended Probing Questions',
                      rtr_explanation: 'RTR Explanation'
                    };
                    return (
                      <div 
                        key={key} 
                        className={`p-4 rounded-lg border ${
                          isViolation 
                            ? 'bg-red-50 border-red-300' 
                            : 'bg-emerald-50 border-emerald-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {isViolation ? (
                            <XCircle className="h-5 w-5 text-red-600" />
                          ) : (
                            <CheckCircle className="h-5 w-5 text-emerald-600" />
                          )}
                          <span className="font-semibold text-sm">
                            {parameterLabels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                          <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                            isViolation 
                              ? 'bg-red-200 text-red-800' 
                              : 'bg-emerald-200 text-emerald-800'
                          }`}>
                            {analysis.final_verdict?.split(' - ')[0] || (isViolation ? 'VIOLATION' : 'PASSED')}
                          </span>
                        </div>
                        
                        {/* Evidence */}
                        {analysis.evidence && (
                          <div className="text-xs text-slate-600 mb-2">
                            <span className="font-medium">Evidence:</span>
                            <span className="italic ml-1">"{analysis.evidence}"</span>
                          </div>
                        )}
                        
                        {/* Reasoning */}
                        {analysis.reasoning && (
                          <div className="text-xs text-slate-700 mb-2">
                            <span className="font-medium">Reasoning:</span>
                            <span className="ml-1">{analysis.reasoning}</span>
                          </div>
                        )}
                        
                        {/* Context info for specific parameters */}
                        {key === 'branding' && (
                          <div className="text-xs text-slate-500 flex gap-3 mt-2">
                            {analysis.is_same_day_followup !== undefined && (
                              <span>Same-day follow-up: {analysis.is_same_day_followup ? 'Yes' : 'No'}</span>
                            )}
                            {analysis.caller_recognized_recruiter !== undefined && (
                              <span>Caller recognized: {analysis.caller_recognized_recruiter ? 'Yes' : 'No'}</span>
                            )}
                          </div>
                        )}
                        
                        {key === 'probing_questions' && analysis.questions_found && (
                          <div className="text-xs text-slate-500 mt-2">
                            <span className="font-medium">Questions found ({analysis.questions_count || analysis.questions_found.length}):</span>
                            <ul className="list-disc list-inside mt-1">
                              {analysis.questions_found.slice(0, 3).map((q, i) => (
                                <li key={i} className="truncate">{q}</li>
                              ))}
                              {analysis.questions_found.length > 3 && (
                                <li className="text-muted-foreground">...and {analysis.questions_found.length - 3} more</li>
                              )}
                            </ul>
                          </div>
                        )}
                        
                        {key === 'rtr_explanation' && analysis.details_included && (
                          <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-2">
                            {Object.entries(analysis.details_included).map(([detail, included]) => (
                              <span key={detail} className={`px-1.5 py-0.5 rounded ${
                                included ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {included ? '✓' : '✗'} {detail.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Final verdict */}
                        {analysis.final_verdict && (
                          <div className={`text-xs font-medium mt-2 pt-2 border-t ${
                            isViolation ? 'border-red-200 text-red-700' : 'border-emerald-200 text-emerald-700'
                          }`}>
                            {analysis.final_verdict}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* QA Notes Tab */}
        <TabsContent value="qa-notes" className="mt-6" data-testid="qa-notes-tab-content">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add Note Form */}
            {canCreateNotes && (
              <Card className="card-elevated lg:col-span-3 border-purple-200">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2 text-purple-700">
                    <Shield className="h-5 w-5" />
                    Add QA Note
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitNote} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                        <Label>Note</Label>
                        <Textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Enter your QA note here... (This will be visible to the user)"
                          rows={3}
                          className="resize-none"
                          data-testid="qa-note-textarea"
                        />
                      </div>
                      <div>
                        <Label>Note Type</Label>
                        <Select value={noteType} onValueChange={setNoteType}>
                          <SelectTrigger data-testid="qa-note-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="feedback">Feedback</SelectItem>
                            <SelectItem value="positive">Positive</SelectItem>
                            <SelectItem value="improvement">Needs Improvement</SelectItem>
                            <SelectItem value="action_required">Action Required</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button 
                          type="submit" 
                          className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                          disabled={isSubmittingNote || !newNote.trim()}
                          data-testid="submit-qa-note-btn"
                        >
                          {isSubmittingNote ? (
                            <span className="animate-spin">⏳</span>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Add Note
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Notes List */}
            <Card className="card-elevated lg:col-span-3">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading">
                    QA Notes ({qaNotes.length})
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={fetchQANotes}>
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingNotes ? (
                  <div className="space-y-4">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : qaNotes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No QA notes for this call yet</p>
                    {canCreateNotes && <p className="text-sm mt-2">Be the first to add a note!</p>}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {qaNotes.map(note => {
                      const typeConfig = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.general;
                      const TypeIcon = typeConfig.icon;
                      
                      return (
                        <div 
                          key={note.id} 
                          className={`p-4 rounded-lg border ${note.is_read_by_user ? 'bg-muted/50' : 'bg-blue-50 border-blue-200'}`}
                          data-testid={`qa-note-${note.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={typeConfig.color}>
                                <TypeIcon className="h-3 w-3 mr-1" />
                                {typeConfig.label}
                              </Badge>
                              {!note.is_read_by_user && (
                                <Badge variant="outline" className="text-amber-500 border-amber-500 text-xs">
                                  New
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(note.created_at)}
                            </span>
                          </div>
                          
                          <p className="text-slate-700 whitespace-pre-wrap mb-3">
                            {note.note_text}
                          </p>
                          
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Shield className="h-3 w-3 text-purple-500" />
                              <span>{note.auditor_name}</span>
                            </div>
                            {note.is_read_by_user && note.read_at && (
                              <div className="flex items-center gap-1">
                                <CheckCircle className="h-3 w-3 text-green-500" />
                                <span>Read {formatDate(note.read_at)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Audit Trail Tab */}
        <TabsContent value="audit-trail" className="mt-6" data-testid="audit-trail-tab-content">
          <div className="space-y-4">
            {/* Summary Stats */}
            {auditSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="card-elevated">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{auditSummary.total}</p>
                      <p className="text-xs text-muted-foreground">Total Activities</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{auditSummary.unique_actors}</p>
                      <p className="text-xs text-muted-foreground">Unique Users</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{auditSummary.action_counts?.view || 0}</p>
                      <p className="text-xs text-muted-foreground">Views</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="card-elevated">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{auditSummary.action_counts?.audio_played || 0}</p>
                      <p className="text-xs text-muted-foreground">Audio Plays</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Filter */}
            <Card className="card-elevated">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">Filter by action:</span>
                  <Select value={auditFilter} onValueChange={setAuditFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="view">Views</SelectItem>
                      <SelectItem value="audio_played">Audio Played</SelectItem>
                      <SelectItem value="reprocess">Reprocessed</SelectItem>
                      <SelectItem value="override">Overrides</SelectItem>
                      <SelectItem value="collaborate">Collaborations</SelectItem>
                      <SelectItem value="review_request">Review Requests</SelectItem>
                      <SelectItem value="qa_note_added">QA Notes Added</SelectItem>
                      <SelectItem value="edit">Edits</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={fetchAuditTrail}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Audit Trail List */}
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle className="text-lg">Activity Log</CardTitle>
                <CardDescription>Complete history of all actions taken on this call</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingAudit ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : auditTrail.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditTrail.map((entry) => {
                      const actionColors = {
                        view: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
                        audio_played: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
                        reprocess: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                        override: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                        collaborate: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
                        review_request: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
                        qa_note_added: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
                        edit: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
                      };
                      
                      return (
                        <div 
                          key={entry.id} 
                          className="flex items-start gap-4 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                        >
                          <Badge className={actionColors[entry.action] || 'bg-muted text-slate-700'}>
                            {entry.action_label}
                          </Badge>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{entry.actor_name}</span>
                              {entry.actor_role && (
                                <Badge variant="outline" className="text-xs capitalize">
                                  {entry.actor_role}
                                </Badge>
                              )}
                            </div>
                            {entry.details && Object.keys(entry.details).length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {Object.entries(entry.details)
                                  .filter(([k, v]) => v && k !== 'page')
                                  .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                                  .join(' • ')}
                              </p>
                            )}
                          </div>
                          
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {entry.created_at ? format(new Date(entry.created_at), 'MMM d, h:mm a') : '-'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Request Review Dialog (for users) */}
      <Dialog open={showReviewRequestDialog} onOpenChange={setShowReviewRequestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Request QA Review
            </DialogTitle>
            <DialogDescription>
              Submit your call for review by the QA team. They will listen to your call and provide detailed feedback.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Call Info */}
            <div className="p-3 bg-muted/50 dark:bg-slate-800 rounded-lg">
              <p className="font-medium text-sm">{call?.filename}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                <span>Score: {call?.average_score?.toFixed(1) || 'N/A'}</span>
                <span className="capitalize">{call?.classification}</span>
              </div>
            </div>
            
            {/* Reason */}
            <div>
              <Label htmlFor="review-reason-detail">Why do you want this call reviewed? *</Label>
              <Textarea
                id="review-reason-detail"
                value={reviewRequestReason}
                onChange={(e) => setReviewRequestReason(e.target.value)}
                placeholder="Explain why you believe this call should be reviewed by QA..."
                className="mt-1"
                rows={4}
              />
            </div>
            
            {/* Expected Outcome */}
            <div>
              <Label htmlFor="review-expected-detail">What outcome do you expect? (Optional)</Label>
              <Textarea
                id="review-expected-detail"
                value={reviewRequestExpected}
                onChange={(e) => setReviewRequestExpected(e.target.value)}
                placeholder="What do you think should change?"
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowReviewRequestDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReviewRequestSubmit}
                disabled={isSubmittingReviewRequest || !reviewRequestReason.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmittingReviewRequest ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Collaborate Dialog (for admins/auditors) */}
      <Dialog open={showCollaborateDialog} onOpenChange={setShowCollaborateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-purple-600" />
              Let&apos;s Collaborate
            </DialogTitle>
            <DialogDescription>
              Review and adjust AI-generated scores based on your expert assessment. Your scores will be combined with AI analysis (60% your input, 40% AI).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current AI Scores Summary */}
            <div className="p-3 bg-muted/50 dark:bg-slate-800 rounded-lg">
              <p className="font-medium text-sm mb-2">Current AI Assessment</p>
              <div className="flex items-center gap-4 text-sm">
                <span>Score: <strong>{call?.average_score?.toFixed(1) || 'N/A'}</strong></span>
                <Badge className={`capitalize ${
                  call?.classification === 'best' ? 'bg-green-500' :
                  call?.classification === 'good' ? 'bg-blue-500' :
                  call?.classification === 'improvement' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}>
                  {call?.classification || 'Unscored'}
                </Badge>
              </div>
            </div>
            
            {/* Score Adjustments */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Adjust Scores (1-10)</Label>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'communication_clarity', label: 'Communication Clarity' },
                  { key: 'tone_professionalism', label: 'Tone & Professionalism' },
                  { key: 'energy_confidence', label: 'Energy & Confidence' },
                  { key: 'listening_skills', label: 'Listening Skills' },
                  { key: 'rapport_building', label: 'Rapport Building' },
                  { key: 'structure_flow', label: 'Structure & Flow' },
                  { key: 'question_quality', label: 'Question Quality' },
                  { key: 'candidate_engagement', label: 'Candidate Engagement' },
                  { key: 'compliance_disclaimers', label: 'Compliance & Disclaimers' },
                  { key: 'overall_effectiveness', label: 'Overall Effectiveness' }
                ].map(({ key, label }) => {
                  const aiScore = call?.quality_scores?.[key];
                  const currentScore = collaborationScores[key];
                  const isNA = aiScore === 'N/A' || aiScore === undefined;
                  
                  return (
                    <div key={key} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`score-${key}`} className="text-sm">{label}</Label>
                        <span className="text-xs text-muted-foreground">
                          AI: {isNA ? 'N/A' : aiScore?.toFixed(1)}
                        </span>
                      </div>
                      <Input
                        id={`score-${key}`}
                        type="number"
                        min="1"
                        max="10"
                        step="0.5"
                        value={currentScore || ''}
                        onChange={(e) => setCollaborationScores(prev => ({
                          ...prev,
                          [key]: e.target.value ? parseFloat(e.target.value) : undefined
                        }))}
                        placeholder={isNA ? 'N/A' : aiScore?.toFixed(1)}
                        className="h-9"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Auditor Notes */}
            <div className="space-y-2">
              <Label htmlFor="collaboration-notes">Your Assessment Notes *</Label>
              <Textarea
                id="collaboration-notes"
                value={collaborationNotes}
                onChange={(e) => setCollaborationNotes(e.target.value)}
                placeholder="Provide your expert assessment of this call. What did the AI miss? What should be highlighted?"
                rows={4}
              />
            </div>
            
            {/* Additional Feedback */}
            <div className="space-y-2">
              <Label htmlFor="collaboration-feedback">Additional Feedback (Optional)</Label>
              <Textarea
                id="collaboration-feedback"
                value={collaborationFeedback}
                onChange={(e) => setCollaborationFeedback(e.target.value)}
                placeholder="Any coaching suggestions or specific improvement areas for the recruiter..."
                rows={3}
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowCollaborateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCollaborationSubmit}
                disabled={isSubmittingCollaboration || !collaborationNotes.trim()}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isSubmittingCollaboration ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Handshake className="h-4 w-4 mr-2" />
                    Submit Collaboration
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Assign User Dialog */}
      <Dialog open={showAssignUserDialog} onOpenChange={setShowAssignUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-green-600" />
              Assign User to Call
            </DialogTitle>
            <DialogDescription>
              Manually assign this call to a user. This will link the call to the selected user&apos;s profile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current assignment info */}
            {call?.user_id && assignedUser && (
              <div className="p-3 bg-muted/50 rounded-lg border">
                <p className="text-sm text-muted-foreground">Currently assigned to:</p>
                <p className="font-medium">{assignedUser.name}</p>
              </div>
            )}
            
            {/* User selector */}
            <div className="space-y-2">
              <Label>Select User</Label>
              {isLoadingUsers ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">Loading users...</span>
                </div>
              ) : (
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <span>{u.name}</span>
                          {u.extension && <span className="text-xs text-muted-foreground">Ext: {u.extension}</span>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignUser}
              disabled={isAssigning || !selectedUserId}
              className="bg-green-600 hover:bg-green-700"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Assign User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Format Dialog */}
      <Dialog open={showEditFormatDialog} onOpenChange={setShowEditFormatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-amber-600" />
              Edit Call Format
            </DialogTitle>
            <DialogDescription>
              Update the call metadata. This can help with user matching if the original file had incorrect format.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                value={editPhoneNumber}
                onChange={(e) => setEditPhoneNumber(e.target.value)}
                placeholder="e.g., 555-1234 or 5551234"
              />
              <p className="text-xs text-muted-foreground">Current: {call?.phone_number || 'Not set'}</p>
            </div>
            
            {/* Extension */}
            <div className="space-y-2">
              <Label htmlFor="edit-extension">Extension</Label>
              <Input
                id="edit-extension"
                value={editExtension}
                onChange={(e) => setEditExtension(e.target.value)}
                placeholder="e.g., 1234"
              />
              <p className="text-xs text-muted-foreground">Current: {call?.extension || 'Not set'}</p>
            </div>
            
            {/* Call Type */}
            <div className="space-y-2">
              <Label htmlFor="edit-call-type">Call Type</Label>
              <Select value={editCallType} onValueChange={setEditCallType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select call type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="screening">Screening</SelectItem>
                  <SelectItem value="interview">Interview</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="job_outreach">Job Outreach</SelectItem>
                  <SelectItem value="pre_screening">Pre-Screening</SelectItem>
                  <SelectItem value="submission_call">Submission Call</SelectItem>
                  <SelectItem value="client_call">Client Call</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Current: {call?.call_type || 'Not set'}</p>
            </div>
            
            {/* Original Filename Info */}
            {call?.original_filename && (
              <div className="p-3 bg-muted/50 rounded-lg border">
                <p className="text-sm font-medium text-slate-700">Original Filename</p>
                <p className="text-xs text-muted-foreground font-mono break-all">{call.original_filename}</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditFormatDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateFormat}
              disabled={isUpdatingFormat}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isUpdatingFormat ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Settings2 className="h-4 w-4 mr-2" />
                  Update Format
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reprocess Dialog with Scoring Guide Selection */}
      <Dialog open={showReprocessDialog} onOpenChange={setShowReprocessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Reprocess Call
            </DialogTitle>
            <DialogDescription>
              Select a scoring guide to reprocess this call. The AI will re-analyze the call using the selected scoring parameters.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Scoring Guide Selection */}
            <div className="space-y-2">
              <Label>Select Scoring Guide</Label>
              <Select
                value={selectedScoringGuide || ''}
                onValueChange={setSelectedScoringGuide}
              >
                <SelectTrigger data-testid="scoring-guide-select">
                  <SelectValue placeholder="Choose a scoring guide" />
                </SelectTrigger>
                <SelectContent>
                  {scoringGuides.map((guide) => (
                    <SelectItem key={guide.id} value={guide.id}>
                      <div className="flex items-center gap-2">
                        <span>{guide.name}</span>
                        {guide.is_default && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                        {guide.total_marks && (
                          <span className="text-xs text-muted-foreground">({guide.total_marks} pts)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scoringGuides.find(g => g.id === selectedScoringGuide)?.description && (
                <p className="text-xs text-muted-foreground">
                  {scoringGuides.find(g => g.id === selectedScoringGuide)?.description}
                </p>
              )}
            </div>
            
            {/* Reprocess Options */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reprocess-transcript"
                  checked={reprocessTranscript}
                  onChange={(e) => setReprocessTranscript(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="reprocess-transcript" className="text-sm font-normal cursor-pointer">
                  Re-identify speakers in transcript
                </Label>
              </div>
            </div>
            
            {/* Info */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
              <p className="font-medium mb-1">What happens:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>AI re-analyzes the call with selected scoring guide</li>
                <li>Scores are recalculated based on guide parameters</li>
                <li>Detailed reasoning provided for each score</li>
                {reprocessTranscript && <li>Speaker identification is refreshed</li>}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReprocessDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleReprocess(reprocessTranscript, selectedScoringGuide)}
              disabled={!selectedScoringGuide}
              data-testid="confirm-reprocess-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Start Reprocessing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Individual Alert Override Dialog */}
      <Dialog open={showIndividualOverrideDialog} onOpenChange={setShowIndividualOverrideDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Mark Alert as Compliant
            </DialogTitle>
            <DialogDescription>
              {selectedAlertForOverride && (
                <span className="block mt-2">
                  <strong>Alert Type:</strong> {selectedAlertForOverride.type?.replace(/_/g, ' ')}
                  <br />
                  <strong>Description:</strong> {selectedAlertForOverride.description}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="override-reason">Override Reason *</Label>
              <Textarea
                id="override-reason"
                placeholder="Explain why this alert should be marked as compliant (e.g., 'Branding was mentioned at 2:15', 'Disclaimer was given verbally')"
                value={individualOverrideReason}
                onChange={(e) => setIndividualOverrideReason(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This will be logged in the call audit trail
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowIndividualOverrideDialog(false);
                setSelectedAlertForOverride(null);
                setIndividualOverrideReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleIndividualAlertOverride}
              disabled={!individualOverrideReason.trim() || overridingAlertIndex !== null}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {overridingAlertIndex !== null ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Compliant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Related Calls Dialog */}
      <Dialog open={showRelatedCallsDialog} onOpenChange={setShowRelatedCallsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5 text-indigo-600" />
              Related Calls for {candidatePhoneNumber}
            </DialogTitle>
            <DialogDescription>
              {relatedCalls.length} other call{relatedCalls.length !== 1 ? 's' : ''} found with this candidate number
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {relatedCalls.length > 0 ? (
              <div className="space-y-3">
                {relatedCalls.map((relatedCall, idx) => {
                  const relatedCallId = relatedCall.id || relatedCall._id;
                  const relatedScore = relatedCall.average_score || relatedCall.quality_score || 0;
                  const scoreColor = relatedScore >= 8 ? 'text-emerald-600' : relatedScore >= 6 ? 'text-amber-600' : 'text-red-600';
                  
                  return (
                    <Card key={relatedCallId || idx} className="hover:bg-muted/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Date & Time */}
                            <div className="text-center min-w-[80px]">
                              <p className="text-sm font-medium">
                                {relatedCall.call_date ? format(new Date(relatedCall.call_date), 'MMM d') : 'N/A'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {relatedCall.call_date ? format(new Date(relatedCall.call_date), 'h:mm a') : ''}
                              </p>
                            </div>
                            
                            {/* Call Type */}
                            <Badge variant="outline" className="capitalize">
                              {relatedCall.call_type?.replace(/_/g, ' ') || 'Unknown'}
                            </Badge>
                            
                            {/* User */}
                            <div className="flex items-center gap-2">
                              <Avatar className="h-6 w-6">
                                <AvatarFallback className="text-xs bg-primary/10">
                                  {relatedCall.user_name?.charAt(0) || relatedCall.recruiter_name?.charAt(0) || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-muted-foreground">
                                {relatedCall.user_name || relatedCall.recruiter_name || 'Unknown'}
                              </span>
                            </div>
                            
                            {/* Duration */}
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {relatedCall.duration ? `${Math.floor(relatedCall.duration / 60)}:${(relatedCall.duration % 60).toString().padStart(2, '0')}` : '-'}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {/* Score */}
                            <div className="text-right">
                              <span className={`text-xl font-bold ${scoreColor}`}>
                                {relatedScore > 0 ? relatedScore.toFixed(1) : '-'}
                              </span>
                              <span className="text-xs text-muted-foreground">/10</span>
                            </div>
                            
                            {/* View Button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setShowRelatedCallsDialog(false);
                                navigate(`${baseUrl}/call/${relatedCallId}`, {
                                  state: { from: location.pathname, fromLabel: 'Back to Related Call' }
                                });
                              }}
                              className="gap-2"
                              data-testid={`view-related-call-${relatedCallId}`}
                            >
                              <ArrowRight className="h-4 w-4" />
                              View
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Phone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="font-medium">No Related Calls Found</p>
                <p className="text-sm text-muted-foreground">
                  This is the only call with this candidate number
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRelatedCallsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Stats Popup - Portal to body to avoid clipping */}
      {showUserPopup && createPortal(
        <>
          {/* Overlay to close popup */}
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setShowUserPopup(false)}
          />
          {/* Popup Card */}
          <div
            className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 w-72 animate-in fade-in zoom-in-95 duration-200"
            style={{
              left: Math.min(userPopupPosition.x - 144, window.innerWidth - 300),
              top: Math.min(userPopupPosition.y, window.innerHeight - 300),
            }}
            data-testid="user-stats-popup"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200 dark:border-slate-700">
              <Avatar className="h-12 w-12 border-2 border-emerald-500">
                <AvatarImage src={getAvatarDisplayUrl(assignedUser || call?.user)} />
                <AvatarFallback className="bg-emerald-600 text-white text-lg">
                  {(assignedUser || call?.user)?.name?.charAt(0) || <User className="h-5 w-5" />}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                  {(assignedUser || call?.user)?.name || 'Unknown User'}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {(assignedUser || call?.user)?.email}
                </p>
              </div>
              <button 
                onClick={() => setShowUserPopup(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>

            {/* Stats Grid */}
            {isLoadingUserStats ? (
              <div className="space-y-3">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <div className="space-y-3">
                {/* Total Calls */}
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Total Calls</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {userStats?.totalCalls ?? '-'}
                  </span>
                </div>

                {/* Average Score */}
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Avg Score</span>
                  </div>
                  <span className={`font-semibold ${
                    userStats?.avgScore >= 8 ? 'text-emerald-600' : 
                    userStats?.avgScore >= 6 ? 'text-amber-600' : 
                    userStats?.avgScore ? 'text-red-600' : 'text-slate-400'
                  }`}>
                    {userStats?.avgScore ? `${userStats.avgScore}/10` : '-'}
                  </span>
                </div>

                {/* Leaderboard Rank */}
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-purple-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Rank</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {userStats?.leaderboardRank ? `#${userStats.leaderboardRank}` : '-'}
                  </span>
                </div>

                {/* Reports To */}
                {userStats?.reportsTo && (
                  <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-cyan-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">Reports To</span>
                    </div>
                    <span className="font-semibold text-slate-900 dark:text-white truncate max-w-[120px]">
                      {userStats.reportsTo}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </>,
        document.body
      )}

      {/* Auditor Stats Popup - Portal to body to avoid clipping */}
      {showAuditorPopup && assignedAuditor && createPortal(
        <>
          {/* Overlay to close popup */}
          <div 
            className="fixed inset-0 z-[9998]" 
            onClick={() => setShowAuditorPopup(false)}
          />
          {/* Popup Card */}
          <div
            className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 w-72 animate-in fade-in zoom-in-95 duration-200"
            style={{
              left: Math.min(auditorPopupPosition.x - 144, window.innerWidth - 300),
              top: Math.min(auditorPopupPosition.y, window.innerHeight - 300),
            }}
            data-testid="auditor-stats-popup"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-lg font-semibold">
                {(assignedAuditor.name || assignedAuditor.full_name)?.charAt(0) || <Shield className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-900 dark:text-white truncate">
                  {assignedAuditor.name || assignedAuditor.full_name || 'Unknown Auditor'}
                </h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {assignedAuditor.email}
                </p>
                <Badge variant="outline" className="mt-1 text-xs border-blue-400 text-blue-600">
                  <Shield className="h-3 w-3 mr-1" />
                  Quality Auditor
                </Badge>
              </div>
              <button 
                onClick={() => setShowAuditorPopup(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>

            {/* Auditor Stats Grid */}
            <div className="space-y-3">
              {/* Users Assigned */}
              <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Users Assigned</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {auditorStats?.totalAssignedUsers ?? '-'}
                </span>
              </div>

              {/* Avg Score */}
              <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Avg Score</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {auditorStats?.avgScore ? `${auditorStats.avgScore}/10` : '-'}
                </span>
              </div>

              {/* Assigned Since */}
              {auditorStats?.assignedSince && (
                <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-300">Assigned Since</span>
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white text-xs">
                    {format(new Date(auditorStats.assignedSince), 'MMM d, yyyy')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
